// 購入前工事費確定モーダル（sections/aicon-install-quote-modal.liquid の器を動かす）。
//
// 進め方は「エリア判定 → 写真判定 → 金額確定」の3ステップ。
// Figma は STEP 4（工事日選択）まで描いているが**本要件のスコープ外**のため 3ステップ。
//
// ★ トークンは sessionStorage に置く（cookie ではない）★
// App Proxy は Cookie を削除するため、そもそも cookie は使えない。加えて LINE 側の
// セッションと混同させないため、タブを閉じたら消える sessionStorage を使う。
//
// ★ トークンはクエリで送る（Authorization ヘッダではない）★
// App Proxy のリクエストには Shopify 自身の Authorization: Bearer <JWT> が載っており、
// アプリが付けたヘッダは届かない。詳細は backend の middleware/requireQuoteSession.ts。
//
// テーマにビルド工程は無いため、素の ES modules で書く（トランスパイル前提にしない）。

/** 見積セッションを載せるクエリパラメータ名。backend の QUOTE_TOKEN_QUERY_KEY と揃える */
const TOKEN_QUERY_KEY = 'quoteToken';

/** sessionStorage のキー。商品ごとに分ける（別商品のモーダルでトークンを使い回さない） */
const storageKey = (modelNo) => `aicon:iq:${modelNo}`;

/**
 * 診断の見積ID（2026-10-17）。比較ページの「この機種を見る」・畳数切替が ?aiq= で運んでくる。
 *
 * ★ 写真見積に「どの診断から来たか」を残すため ★
 *   ここで新しい見積ができると注文はそちらに紐づき、診断（何をすすめたか）へ
 *   辿れなかった（#1158・#1159 の調査）。backend が実在と origin を確かめてから保存する
 *   （services/quote/diagnosisQuoteLink.ts）。形の判定はそちらと揃える。
 *
 * ★ ページを開いたときに1回だけ読んで持っておく ★
 *   トークン切れ・「見積が見つからない」で見積を作り直すときにも同じ ID を送るため
 *   （forgetSession → ensureSession）。
 */
const DIAGNOSIS_QUERY_KEY = 'aiq';
const DIAGNOSIS_ID_PATTERN = /^c[a-z0-9]{20,32}$/;
/**
 * 診断の見積IDを載せるカート属性（2026-10-18）。backend の ATTR_DIAGNOSIS_QUOTE_ID と揃える。
 * 写真見積を通らない購入（「写真なしで進む」等）は見積ができず、上の ?aiq= を見積に
 * 残せない（#1170）。カートに載せ、注文の取り込みで orders.diagnosisQuoteId に残す。
 */
const DIAGNOSIS_CART_ATTR = '__aicon_diagnosis_quote_id';
const readDiagnosisQuoteId = () => {
  try {
    const id = new URLSearchParams(window.location.search).get(DIAGNOSIS_QUERY_KEY) || '';
    return DIAGNOSIS_ID_PATTERN.test(id) ? id : null;
  } catch {
    return null;
  }
};

/**
 * 撮ってもらう写真。
 *
 * ★ Figma は2枚だが実装は4枚 ★
 * backend の `PHOTO_SHOT_KINDS` と揃える。「実装が正」の判断（ヒアリングQ2）。
 * `hint` は撮り方の助言で、解析の精度に直結する。
 */
/**
 * 撮ってもらう4枚。
 *
 * `why` は診断SPA（/web/diagnosis の PhotoView）と同じ「なぜ撮るのか」の一言。
 * 画面の見た目もあちらに合わせている（日高さん指摘 2026-08-19）ので、
 * 文言を変えるときは両方を見比べること。
 */
const SHOTS = [
  {
    kind: 'label',
    title: '室内機のラベル',
    hint: '本体側面のシールの文字が読める位置で',
    why: 'ラベルの型番から、いまの機種の能力とサイズを正確に読み取ります。',
    /**
     * 撮影イメージ（既存 AiCON の ChatPhotoCapture の PhotoGuide と同じ内容・同じ画像）。
     * 文言・画像を変えるときは frontend/src/components/chat/ChatPhotoCapture.tsx も見ること。
     */
    guide: {
      lead: '本体の底面か、右側面にあるこのようなラベルを撮影してください',
      // ラベルだけ2枚並列（既存の type==='indoor' 専用レイアウト）
      pair: [
        { label: '底面のラベル', src: '/guide/aircon_photo_1_bottom_label.jpg' },
        { label: '右側面のラベル', src: '/guide/aircon_photo_2_right_label.jpg' },
      ],
      point: {
        text: 'ラベルの文字や数字がはっきりと読めるようにピントを合わせて撮影してください。',
        src: '/guide/aircon_photo_3_label_closeup.jpg',
      },
    },
    /**
     * ★ 発売から12年で「再利用できない」に倒れることを先に伝える（日高さん指摘 2026-08-28）★
     *
     * 型番から発売年を引き、**12年以上前なら配管カバーを再利用不可へ上書き**する判定が
     * 既にある（backend の `evaluateOutdoorCoverAgeOverride`。AI が「交換推奨」と見た
     * ときに効く）。理由は、写真では塗装も破損も見えないカバーでも 12年ものは取り外す
     * ときに割れるためで、現場で割れてから伝えると**当日の追加請求**になる。
     *
     * この判定は STEP3 まで進んで初めて画面に出る。ラベルを撮った時点で伝えておけば、
     * 「新品カバーが選ばれているのはなぜか」を後で探さずに済む。
     *
     * ★ 判定そのものは書かない ★
     *   ここは「そういう規則がある」ことの明示だけ。年数の判定は backend が持ち、
     *   テーマに年を持たせると二重管理になる（金額に効く判定はサーバに寄せる方針）。
     *
     * ★ 室内の配管カバーは対象外（2026-10-26）★
     *   室内カバーは劣化が少ないので12年の判定を外した（#1192 の後に決定）。
     *   「配管カバー」とだけ書くと室内も交換必須に読めるので、室外と書き分ける。
     *
     * ★ 大事なところだけ太字にする（日高さん指摘 2026-10-26・一続きの文だと読みにくい）★
     *   文字列と `{ b: '…' }`（太字）を並べた配列。描画は `noteHtml` が1つずつ escape する。
     */
    note: [
      'ご使用中の機種が',
      { b: '発売から12年以上' },
      '経っている場合、既存の',
      { b: '室外の配管カバーや架台' },
      'は取り外しの際に破損するおそれがあるため、お使いのときは',
      { b: '交換が必須' },
      'になります（交換費用は STEP3 の追加工事に含めてご提示します）。',
      { b: '室内の配管カバーは、年数にかかわらず再利用をお選びいただけます。' },
    ],
  },
  {
    kind: 'outlet',
    title: 'コンセント',
    hint: '形と表示（100V / 200V）が読めるように近づけて',
    why: '形と電圧を見て、コンセント工事が要るかを判定します。',
    guide: {
      lead: 'エアコンのプラグをコンセントから抜いて、差し込み口の穴の形が分かるように近くで撮影してください',
      example: {
        src: '/guide/aircon_outlet_photo_1_plug.jpg',
        caption: 'プラグを抜いて、コンセントの差し込み口が見える状態で撮影してください',
      },
      point: { text: '★ 必ずプラグを抜いてから撮影してください（穴の形状が見えないと判定できません）' },
      reference: {
        src: '/guide/aircon_outlet_illustration_2_reference_table.jpg',
        caption:
          'エアコン用コンセントは上記の4種類があります。穴の形で電圧（100V/200V）と電流（15A/20A）が決まります',
      },
    },
  },
  {
    kind: 'indoor',
    title: '室内機まわり',
    hint: '設置予定の壁と、その周囲が入るように',
    why: '配管の経路と、壁までの距離を見ます。',
    guide: {
      lead: 'エアコンの正面に立ち、2〜3歩下がって、人の目線の高さから撮影してください',
      example: {
        src: '/guide/aircon_room_photo_1_main.png',
        caption: '天井〜床まで写っている状態が理想です。障害物も含めて撮影してください。',
      },
      point: { text: '天井から床まで全体が写るように。障害物も含めて、ピントを合わせて撮影してください。' },
      bad: [
        { src: '/guide/aircon_room_photo_3_bad_no_ceiling.png', caption: '天井が入っていない' },
        { src: '/guide/aircon_room_photo_4_bad_no_floor.png', caption: '床が入っていない' },
        { src: '/guide/aircon_room_photo_5_bad_no_obstacle.jpg', caption: '床の障害物が写っていない' },
      ],
      warning: '写真が不鮮明・一部が切れている場合、再度ご提出をお願いすることがあります。',
    },
    /**
     * 撮る前に伺うこと（web版 AiCON の PhotoView と同じ設問・同じ順番）。
     * frontend/src/components/diagnosis/views/PhotoView.tsx:850 が正。
     * 配管の質問は金額を動かさない（追加で撮ってもらうかを決めるためのもの）。
     */
    guards: [
      {
        key: 'pipeVisible',
        label: '配管（配管カバー）は室内から見えていますか？',
        options: [
          { value: 'yes', label: '見えている' },
          { value: 'no', label: '見えない' },
        ],
        // 「見えない」を選び直したら、下の設問の回答を捨てる
        clears: { no: ['pipeAllInPhoto'] },
      },
      {
        key: 'pipeAllInPhoto',
        label: '室内機の正面写真に、配管は全部写せますか？',
        when: { pipeVisible: 'yes' },
        examples: {
          heading: 'こんな場合は「一部隠れている」を選んでください',
          items: [
            { src: '/guide/aircon_front_photo_1_next_room_route.jpg', caption: '隣の部屋を経由' },
            { src: '/guide/aircon_front_photo_2_tokonoma_route.jpg', caption: '床の間を経由' },
            { src: '/guide/aircon_front_photo_3_engawa_route.jpg', caption: '縁側を経由' },
            { src: '/guide/aircon_front_photo_4_closet_route.jpg', caption: '押入れを経由' },
          ],
        },
        options: [
          { value: 'yes', label: '全部写せる' },
          { value: 'no', label: '一部隠れている' },
        ],
        noteWhen: {
          no: '正面写真のあと、写りきらない配管部分を追加で撮影してください（複数枚アップロードできます）。',
        },
      },
    ],
  },
  {
    kind: 'outdoor',
    title: '室外機の置き場所',
    hint: '置き場所と配管の経路が分かるように',
    why: '置き方（地面・屋根・二段）で追加工事が変わるため確認します。',
    guide: {
      lead: '室外機から配管をたどって、配管が壁に入るところまで撮影してください',
      example: {
        src: '/guide/outdoor_example.jpg',
        caption: '室外機から配管が壁に入るところまでの経路が分かる写真が理想です',
      },
      point: {
        text: '曲がっている部分も含めて撮影してください。配管カバーがある場合はカバーごと撮影してください。',
      },
    },
    /**
     * ★ 階数と架台は金額に効く ★
     *   1階と答えれば高所作業費が外れ、架台の種類で架台費が決まる
     *   （backend/src/services/analysis/guardAnswers.ts）。
     *   写真からの推定より本人の回答のほうが確実で、当日の食い違いも減る。
     */
    guards: [
      /*
       * ★ 聞くのは「お部屋の階」。室外機の階ではない（2026-09-15 に変更）★
       *   旧:「室外機の設置は何階ですか？」。**2階→1階の工事は室外機が1階に置かれる**ので、
       *   お客様が正しく「1階」と答えると backend が高所作業費を消していた（#1049）。
       *   高所作業の要否は配管がどこまで上がるかで決まり、その上端は室内機側にある。
       *   催事フロー（frontend/src/lib/event/install-questions.ts）と同じ聞き方に揃えた。
       */
      {
        key: 'floor',
        label: '設置するお部屋は何階ですか？',
        options: [
          { value: '1階', label: '1階' },
          { value: '2階', label: '2階' },
          { value: '3階以上', label: '3階以上' },
        ],
        // 1階に直したらベランダの回答は意味を失う。残すと古い答えで金額が動く
        clears: { '1階': ['balcony'] },
        hideWhenDownflow: true,
      },
      /*
       * ★ 復活させた質問（2026-09-15）★
       *   LIFF 版の「室外機はベランダに置く予定ですか？」は 2026-07-23 に
       *   「架台の有無で把握でき冗長」として廃止された。その結果
       *   **2階以上で高所作業費を足す経路が1つも無くなり**、AI が出したときしか
       *   付かなくなっていた。架台の質問は屋根置き・壁掛け等は拾うが、
       *   **直置きが地面かベランダか**は区別できない ── そこが要否を分ける。
       *   判定は催事フロー（install-estimate.ts）の「2階以上+ベランダなし」と同じ。
       */
      {
        key: 'balcony',
        label: '室外機はベランダに置きますか？',
        when: { floor: ['2階', '3階以上'] },
        /*
         * ★ 「わからない」は出さない（2026-09-18 日高さん指示）★
         *   この選択肢は催事フロー（`frontend/src/lib/event/install-questions.ts` の
         *   「そのお部屋にベランダはありますか？」）から持ってきたもので、
         *   対面で聞く催事には要るが **EC では要らない**。
         *
         *   ★ 催事は別定義なので触らない ★
         *     催事は設問・受け口・見積の3箇所に自前の `'unknown'` を持っている
         *     （`install-questions.ts` / `api/event/orders.ts` の
         *     `z.enum(['yes','no','unknown'])` / `event-order.service.ts`）。
         *     いずれも `BALCONY_ANSWERS` を参照していない。ここを消しても催事は動く。
         *
         *   ★ backend の `BALCONY_ANSWERS` も**変えない** ★
         *     `unknown` を外しても**挙動が変わらない**ので触る意味が無い。
         *     `BALCONY_ANSWERS` を読むのは `parseGuardAnswers` だけ＝このテーマが叩く
         *     `/photos/finish` 専用で、`unknown` は `applyGuardAnswers` のどの分岐にも
         *     当たらない（AI 判定に委ねる＝高所作業費を足しも引きもしない）。
         *     外した場合に効くのは**古い JS を掴んだタブが `unknown` を送ってきたとき**
         *     だけだが、`pick()` が捨てる＝未回答扱いで結論は同じ。
         *     ∴ 受け口は広いままにして、出さないのは画面側だけで足りる。
         */
        options: [
          { value: 'yes', label: 'ベランダに置く' },
          { value: 'no', label: 'ベランダ以外（地面・屋根・壁など）' },
        ],
        noteWhen: {
          no: 'はしご・足場での作業になるため、高所作業費が加算されます。',
        },
        hideWhenDownflow: true,
      },
      {
        key: 'standHas',
        label: '室外機の架台（設置台）はありますか？',
        options: [
          { value: 'yes', label: 'ある' },
          { value: 'no', label: 'ない（直置き・立ち下ろし）' },
        ],
        clears: { no: ['standType'] },
        // ★ 図は質問の「下」に置く（web版 PhotoView と同じ）★
        //   先に図を見せると「架台とは何か」を読む前に選ばせることになる。
        //   質問→図の順にすると、迷った人だけが図を見て選び直せる。
        figureAfter: {
          src: '/guide/outdoor-placement-guide.jpg',
          alt: 'エアコン室外機の設置パターン（直置き・立ち下ろし・屋根置き・天吊り・壁掛け・二段置き）',
          caption:
            '「架台」は室外機を屋根・壁・2段などに載せる金属製の台です。地面・ベランダに直置きなら「ない」、金属の台に載っていれば「ある」を選んでください。',
        },
      },
      {
        key: 'standType',
        label: '架台の種類は？',
        when: { standHas: 'yes' },
        options: [
          { value: 'roof', label: '屋根置き' },
          { value: 'wall', label: '壁掛け' },
          { value: 'twoTier', label: '二段置き' },
          { value: 'ceiling', label: '天吊り' },
        ],
      },
      {
        key: 'outdoorOneShot',
        label: '室外機と配管の接続部を、1枚の写真に写せますか？',
        examples: {
          heading: '下の例のように1枚に収まらない場合は「写しきれない」を選んでください',
          items: [
            { src: '/guide/outdoor_pipe_long_example1.jpg', caption: '配管が壁沿いに長い場合の例' },
            { src: '/guide/outdoor_pipe_long_example2.jpg', caption: '配管が2階まで上がっている場合の例' },
          ],
        },
        options: [
          { value: 'yes', label: '1枚で写せる' },
          { value: 'no', label: '写しきれない' },
        ],
        clears: { yes: ['outdoorSameWall'] },
      },
      {
        key: 'outdoorSameWall',
        label: '室内機と室外機は、同じ壁面にありますか？',
        when: { outdoorOneShot: 'no' },
        figure: {
          src: '/guide/outdoor_oneshot_guide.jpg',
          alt: '標準設置（同一壁面）と特殊設置（異壁面・角）の参考図',
          caption:
            '左のように室外機と配管穴が同じ壁面にある場合は「同じ壁面」を、右のように角や別の壁面にある場合は「別の壁面」を選んでください。',
        },
        options: [
          { value: 'yes', label: '同じ壁面' },
          { value: 'no', label: '別の壁面' },
        ],
      },
    ],
  },
];

/**
 * ガード質問の回答を置く sessionStorage のキー。
 *
 * ★ 商品ごとに分ける（日高さん指示 2026-08-28）★
 * もとは全商品で1つの `aicon:iq:guards:v1` を共有していた。設問は
 * 「配管は室内から見えているか」「正面写真に配管が全部写せるか」で、
 * **答えは部屋ごとに違う**。共有していると、別の部屋に置く2台目を見に行った人に
 * 前の部屋の回答が復元される。回答は高所作業費・架台費と**撮影できる枚数の上限**に
 * 効くので、写真と噛み合わない額で確定しうる（`forgetPhotos()` のコメントと同じ懸念が、
 * 商品を移動しただけでは `forgetPhotos()` が走らないぶん残っていた）。
 *
 * セッショントークン（`storageKey`）と同じ粒度に揃える。商品を変えたら聞き直す。
 * 旧キーは誰も読まなくなるだけで、タブを閉じれば消える（sessionStorage のため）。
 *
 * ★ v1 → v2（2026-09-15）★
 * `floor` の**意味**を変えたのでキーを分ける。旧 v1 は「室外機の設置は何階ですか？」への
 * 回答で、新しい設問は「設置するお部屋は何階ですか？」。**同じ '1階' でも指すものが違う**。
 * 2階→1階のお客様は旧設問に正しく「1階」（＝室外機は1階）と答えているので、そのまま
 * 読み戻すと新設問では「お部屋が1階」と解釈され、高所作業費が消える（#1049 の再現）。
 * しかも設問は回答済みに見えるため、お客様も気づけない。
 *
 * ∴ **設問文や選択肢の意味を変えたら必ずここを上げる**。値の形が同じでも読み戻さない。
 */
const guardStorageKey = (modelNo) => `aicon:iq:guards:${modelNo}:v2`;

/** 送る前に縮める上限。長辺のピクセル数 */
/**
 * 配管カバーの色。
 * web版 CoverView の COVER_COLORS と同じ並び・同じ表記・同じスウォッチ色。
 * backend の `COVER_COLORS`（coverContext.service.ts）とも一致させること
 * （ずれると選んだ色がサーバで捨てられる）。
 */
const COVER_COLORS = [
  { name: 'アイボリー', chip: '#efe7d3' },
  { name: 'ブラウン', chip: '#6b4a2b' },
  { name: 'ホワイト', chip: '#ffffff' },
  { name: 'ブラック', chip: '#333333' },
  { name: 'グレー', chip: '#9aa0a6' },
];

const MAX_IMAGE_EDGE = 1600;
/** JPEG の品質。0.8 で解析精度を保ちつつ十分に軽くなる */
const IMAGE_QUALITY = 0.8;

const STEP_LABELS = {
  1: 'STEP 1 / 3 エリア確認',
  2: 'STEP 2 / 3 写真の確認',
  3: 'STEP 3 / 3 判定結果',
};


class AiconInstallQuote extends HTMLElement {
  connectedCallback() {
    this.modelNo = this.dataset.modelNo;
    this.proxyBase = this.dataset.proxyBase || '/apps/aicon';
    this.diagnosisQuoteId = readDiagnosisQuoteId();
    // 同じ見積に何度も付けに行かない（backend が受け付けなかった ID でも1回で止める）
    this.diagnosisLinkTried = new Set();

    this.overlay = this.querySelector('[data-iq-overlay]');
    this.panel = this.querySelector('[data-iq-panel]');
    this.stepLabel = this.querySelector('[data-iq-step-label]');

    // ★ 郵便番号の入力だけを消し、ステップは3つのまま（日高さん指摘 2026-08-21）★
    //   一度 2ステップにしたが、STEP1 は「写真で確認する／写真なしで進む」を
    //   選ぶ画面として必要。消したのは郵便番号の入力とエリア判定の結果だけ
    //   （2026-10-27 に設定ごと削除。STEP1 は常に進め方を選ぶ画面）。
    /**
     * STEP1（進め方を選ぶ画面）のヘッダー文。
     * 文言は liquid の設定が持つ。ここに直接書くと同じ文字列が2箇所になる。
     */
    this.choiceStepLabel = (this.dataset.choiceStepLabel || '').trim();
    this.progress = this.querySelector('[data-iq-progress]');
    this.step1Panel = this.querySelector('[data-iq-step-panel="1"]');
    this.backButton = this.querySelector('[data-iq-back]');
    this.errorBox = this.querySelector('[data-iq-error]');

    this.step = 1;
    this.session = null;
    /** セッション発行の in-flight。二重に叩かないため（Quote が2件できる） */
    this.sessionPromise = null;

    /**
     * 配管カバーの仕上げ。
     *   coverContext … サーバの判定（再利用できるか）。**写真が変わったら捨てる**
     *   coverPlan / coverColor … お客様の選択
     */
    this.coverContext = null;
    this.indoorContext = null;
    this.coverPlan = null;
    this.coverColor = null;
    this.interiorPlan = null;
    /**
     * 室外機の架台（#1117 / 2026-10-08）。
     *   standContext … サーバの判定（架台があるか・種類・再利用できるか）。写真や回答が変わったら捨てる
     *   standPlan    … お客様の選択（'reuse' | 'replace'）
     */
    this.standContext = null;
    this.standPlan = null;

    // --- STEP 2: 写真 ---
    this.photosRoot = this.querySelector('[data-iq-photos]');
    /** kind -> [{ dataUrl, base64 }, ...]（複数枚を許す kind があるため常に配列） */
    this.photos = new Map();
    /**
     * 撮る前に伺う質問への回答（web版 AiCON の PhotoView と同じ設問）。
     * 前回の続きから戻ってきた人に同じ質問を繰り返さないよう読み戻す。
     */
    this.guardAnswers = this.restoreGuardAnswers();
    /** 「写真を撮影・選択する」を未回答のまま押した shot の kind。赤く示すのに使う */
    this.guardAttempted = null;
    // 1枚ごとの解析状態。kind → 'idle' | 'analyzing' | 'done' | 'retake'
    this.shotState = new Map();
    // 1枚ごとの認識結果（サーバーが組み立てた表示用の行）
    this.shotRecog = new Map();
    // 撮り直しをお願いする理由
    this.shotReason = new Map();
    // 撮り直しで古い応答を捨てるための世代
    this.shotEpoch = new Map();
    // ★ 1枚ずつ見せる（日高さん指摘 2026-08-19）★
    //   4枠を一度に並べると「何をどう撮ればいいか」が伝わらず、既存の診断SPA と
    //   見た目も揃わない。あちら（PhotoView）と同じく1枚ずつ進む形にした。
    this.shotIndex = 0;
    this.airoSrc = this.dataset.airoSrc || '';
    this.analyzing = false;
    this.analysis = null;
    this.renderPhotos();

    // --- STEP 3: 判定結果 ---
    this.resultRoot = this.querySelector('[data-iq-result]');
    this.quote = null;

    this.querySelector('[data-iq-open]')?.addEventListener('click', () => this.open());

    // ★ STEP1 の選択肢（2026-08-21）★
    //   liquid が静的に置いたボタンなので、ここで一度だけ配線する。
    this.querySelector('[data-iq-choose-photos]')?.addEventListener('click', (event) => {
      if (!this.canUsePhotos()) {
        this.showPhotoUnavailable(event.currentTarget);
        return;
      }
      this.goTo(2);
    });
    this.querySelector('[data-iq-choose-skip]')
      ?.addEventListener('click', (event) => this.contactStaff(event.currentTarget));

    // 延長保証規約の簡易モーダル。開いたら背景と × で閉じる
    this.querySelectorAll('[data-iq-terms-close]').forEach((el) =>
      el.addEventListener('click', () => this.closeTerms()),
    );
    this.querySelector('[data-iq-close]')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    this.backButton?.addEventListener('click', () => this.goBack());

    this.bindAddToCart();

    // Esc で閉じる。モーダルが開いているときだけ拾う
    this.onKeydown = (event) => {
      if (event.key === 'Escape' && !this.panel.hidden) this.close();
    };
    document.addEventListener('keydown', this.onKeydown);

    // ★ 戻るで帰ってきたら「押させない」を解く ★
    //   bfcache は DOM ごと復元するため、カートへ移る直前に掛けた disabled が
    //   そのまま残る（`holdButton()` のコメント参照）。
    //   `persisted` を見ずに毎回解くのは、Horizon が cross-document view transition
    //   を使っていて通常の遷移でも pageshow が飛ぶため（assets/scroll-container.js:51）。
    //   通常の読み込みでは掛かっているものが無いので、解いても何も起きない。
    this.onPageShow = (event) => this.recoverFromRestore(event?.persisted === true);
    window.addEventListener('pageshow', this.onPageShow);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  // ------------------------------------------------------------------
  // 「カートに入れる」からの起動（2026-08-17 レビュー会の決定）
  // ------------------------------------------------------------------

  /**
   * 購入ボックスのカート投入を横取りして、このモーダルを開く。
   *
   * ★ なぜ独立ボタンをやめたのか ★
   * 商品ページに「カートに入れる」と「追加費用がないか確定します」が並び、
   * どちらを押せばいいのか分からない状態だった。導線を1本にするため、
   * **カートに入れようとした瞬間に確認を挟む**方式へ変えた。
   *
   * ★ 横取りするのは工事つきを選んだときだけ ★
   * 「本体のみ」は工事をしないので、追加工事費を診断しても意味がない。
   * 購入方法のラジオ（[data-aicon-methods]）の data-with-install を見て判断する。
   *
   * ★ JS が動かない環境では素通しになる ★
   * リスナーを付けられなければ従来どおりカートに入る。押しても何も起きない
   * ボタンにはならない（購入できない方が損失が大きい）。
   */
  bindAddToCart() {
    // 購入ボックスは1ページに1つ（id は固定されている）
    this.addButton = document.querySelector('[data-aicon-add]');
    this.addForm = this.addButton?.closest('form');

    /** 「診断せずに進む」を押したあとの再送信を横取りしないためのフラグ */
    this.bypassIntercept = false;

    // ★ 出口の配線は購入フォームの有無に依らず先に済ませる ★
    //   フォームが見つからないときに配線を飛ばすと、押しても何も起きないボタンが
    //   残る。skipToCart 側でフォームが無い場合を扱う。
    this.querySelector('[data-iq-skip-all]')?.addEventListener('click', () => this.skipToCart());

    // ★ 購入方法が変わったら写真判定の可否表示を戻す ★
    //   いまはオーバーレイがクリックを吸うため切り替えは閉じている間にしか起きず、
    //   open() 側の解除だけで足りる。将来オーバーレイの外で切り替えられるように
    //   なっても壊れないよう、変更そのものも拾っておく（二重でも無害）。
    document
      .querySelector('[data-aicon-methods]')
      ?.addEventListener('change', () => this.resetPhotoAvailability());

    if (!this.addForm) return;

    /*
     * ★ ここで submit を横取りしない（日高さん指摘 2026-08-21）★
     * 以前はこの form に submit リスナーを足して preventDefault + open していたが、
     * 同じ form には購入ボックス（sections/aicon-product-buybox.liquid）の
     * submit リスナーも付いており、そちらが /cart/add.js を叩いて /cart へ遷移する。
     * preventDefault は同じ要素の他のリスナーを止めないため、両方が走って
     * 「モーダルが開いた直後にカートへ飛ぶ」状態になっていた。
     *
     * 送信の可否を決めるのは購入ボックス側の1箇所に寄せた。あちらが
     * shouldIntercept() / bypassIntercept / open() を見て呼ぶ。
     * このクラスは open/close と診断の中身だけを持つ。
     */
  }

  /**
   * 工事つきの購入方法が選ばれているか。選択UIが無い商品では横取りしない。
   *
   * ★ 新規取り付けも横取りしない（日高さん指示 2026-10-01）★
   * 新規は写真診断の対象外（canUsePhotos）で、モーダルに出せるのは
   * 「レジに進む」1つだけだった。購入ボックスの「レジに進む」を押した直後に
   * 同じボタンをもう一度押させるだけになるので、本体のみと同じく素通しにする。
   * 素通し後は購入ボックスが本体・工事・2階→1階の追加工事をまとめて入れる
   * （モーダルの skipToCart() と同じ経路）。
   */
  shouldIntercept() {
    const checked = document.querySelector('[data-aicon-methods] input:checked');
    if (!checked) return false;
    if (checked.dataset.installType === 'new') return false;
    return checked.dataset.withInstall === 'true';
  }

  /**
   * いま選ばれている購入方法の種別。'replace' | 'new' | 'solo' | null。
   *
   * 購入ボックスのラジオが持つ data-install-type を読む
   * （sections/aicon-product-buybox.liquid で variant.title から決めている）。
   * 属性が無い古いテーマでは null になり、判定は従来どおり素通しになる。
   */
  currentInstallType() {
    const checked = document.querySelector('[data-aicon-methods] input:checked');
    return checked?.dataset.installType || null;
  }

  /**
   * 購入ボックスで「2階→1階」が選ばれているか。
   *
   * 工事条件の欄は `[data-aicon-methods]` の**外**にある
   * （sections/aicon-product-buybox.liquid の ★ 参照。中に入れると
   *   `[data-aicon-methods] input:checked` を読む7箇所が壊れる）。
   */
  downflowSelected() {
    // 取替え・新規のどちらでも乗る（新規は 2026-09-23 から。本体のみは工事が無い）
    const type = this.currentInstallType();
    if (type !== 'replace' && type !== 'new') return false;
    const cond = document.querySelector('[data-aicon-cond]');
    if (!cond || cond.hidden) return false;
    return cond.querySelector('[data-aicon-cond-input]:checked')?.dataset.cond === 'downflow';
  }

  /**
   * 2階→1階を選んだ人に入れる追加工事の行。選ばれていなければ空配列。
   *
   * ★ 写真判定を完走した場合はこれを使わない ★
   * `buildCartItems()` は確定した見積り（q.additionalItems）だけを使い、
   * `clearAiconLines()` が先に追加工事を全部消す。∴ **AI の判定が正**になり、
   * プリセットの「配管延長 3m」は AI の「配管延長 6m」へ自動で置き換わる。
   * ここが使われるのは写真判定を通さずカートへ入れる出口だけ。
   *
   * @param {string} setId 同じ投入で作った組ID（本体・工事と同じものを渡す）
   * @returns {{id:number, quantity:number, properties:object}[]}
   */
  downflowItems(setId) {
    if (!this.downflowSelected()) return [];
    const cond = document.querySelector('[data-aicon-cond]');
    const additionalId = (cond?.dataset.additionalId || '').trim();
    const raw = cond?.querySelector('[data-aicon-cond-input]:checked')?.dataset.presetLines || '';
    if (!additionalId || !raw) return [];
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (_e) {
      return [];
    }
    return parsed.reduce((items, line) => {
      const qty = Number(line.q);
      // 数量が壊れていたら入れない。0や小数で入れると請求額が変わる
      if (!Number.isInteger(qty) || qty <= 0) return items;
      return [
        ...items,
        {
          id: Number(additionalId),
          quantity: qty,
          // 費目ごとに properties を変えて別行にする（同じにすると1行に潰れて費目が読めない）。
          // 組ID を載せないと aicon-cart-pair.js が入れた直後に 0 にする（2026-08-26）
          properties: { 費目: String(line.label || '追加工事'), _aicon_set: setId },
        },
      ];
    }, []);
  }

  /**
   * 写真判定に進めるか。
   *
   * ★ 新規取り付けは対象外 ★
   * 写真判定は「いま付いているエアコンと、その周りの状況」を見て追加工事を出す仕組みで、
   * 既設機が無い新規取り付けでは判定材料がそろわない。
   * ボタンを消さずにバリデーションで止めるのは、押せるのに何も起きない状態を作らないため。
   */
  canUsePhotos() {
    return this.currentInstallType() !== 'new';
  }

  /**
   * 診断せずにカートへ入れる。
   * 横取りを解除して、購入ボックスのフォームをそのまま送信する。
   */
  skipToCart() {
    this.bypassIntercept = true;
    this.close();
    // 購入フォームが見つからない場合（設定でトリガーボタンを出しているだけの
    // ページなど）は閉じるだけにする。押しても無反応にはしない
    if (!this.addForm) {
      this.bypassIntercept = false;
      return;
    }
    try {
      // requestSubmit があれば使う（submit イベントを発火させたいので form.submit() は使わない。
      // あちらはリスナーを飛ばすため、テーマ側の他の処理も一緒に飛ぶ）
      if (typeof this.addForm.requestSubmit === 'function') {
        this.addForm.requestSubmit(this.addButton);
      } else {
        this.addButton.click();
      }
    } finally {
      // ★ フラグを持ち越さない ★
      //   submit イベントは同期で流れるので、ここに来た時点で横取りリスナーは
      //   通過し終わっている。true のまま残すと、カートから**戻るボタンで**この
      //   ページへ帰ってきたとき（bfcache で JS の状態ごと復元される）に
      //   「カートに入れる」がモーダルを開かず素通ししてしまう。
      this.bypassIntercept = false;
    }
  }

  // ------------------------------------------------------------------
  // 開閉
  // ------------------------------------------------------------------

  open() {
    this.overlay.hidden = false;
    this.panel.hidden = false;

    // ★ 背景を DOM から消さず、スクロールだけ止める（NFR-202）
    //   position:fixed で固定すると閉じたときにスクロール位置が飛ぶため、
    //   位置を控えてから固定し、閉じるときに戻す
    this.scrollY = window.scrollY;
    document.body.style.top = `-${this.scrollY}px`;
    document.body.classList.add('aicon-iq-locked');

    this.panel.focus?.();

    // ★ 開いた直後に STEP1 の中身が出ていなかった（2026-08-21 の不具合）★
    //   パネルの出し入れとヘッダーの文言を持っているのは goTo() だが、open() は
    //   goTo() を通らない。そのため liquid が出した初期状態
    //   （show_area_step が off のときは STEP1 パネルに hidden が付いている）が
    //   そのまま残り、ボトムシートが見出しとリードだけの数行の高さで止まっていた。
    //   （設定は 2026-10-27 に削除。いまは STEP1 パネルに hidden は付かない）
    //
    //   ★ goTo() ではなく syncStepView() を呼ぶ ★
    //     goTo(3) は enterResult() を呼び、/quote/confirm を投げ直す。
    //     STEP3 で閉じて開き直すたびに再確定が走るので、
    //     DOM を合わせるだけの処理に分けてある。
    this.syncStepView();

    // ★ 開くたびに判定結果を描き直す ★
    //   購入方法のラジオは**モーダルの外**にあり、オーバーレイがクリックを吸うので
    //   切り替えは必ず「閉じてから」起きる。取替えで開いた画面をそのまま残すと、
    //   新規に変えて開き直したときに「写真で確認する」「◯◯円引き」が出たままになる。
    this.resetPhotoAvailability();

    // ★ ここでセッションを発行しない ★
    //   発行すると backend に Customer と Quote が1行ずつ入る。開く契機が
    //   「カートに入れる」に変わり、見るだけで閉じる人も必ず通るようになったため、
    //   開いた瞬間の発行は不要な行を量産する。発行は必要になった時点（写真の送信など）で行う。
  }

  close() {
    this.stopCheckTicker();
    this.overlay.hidden = true;
    this.panel.hidden = true;

    document.body.classList.remove('aicon-iq-locked');
    document.body.style.top = '';
    window.scrollTo(0, this.scrollY || 0);
  }

  // ------------------------------------------------------------------
  // ステップ遷移
  // ------------------------------------------------------------------

  goTo(step) {
    const next = Math.min(3, Math.max(1, step));
    this.step = next;

    this.syncStepView();

    if (next === 2) this.renderPhotos();
    if (next === 3) this.enterResult();

    this.dispatchEvent(new CustomEvent('aicon-iq:step', { detail: { step: next } }));
  }

  /**
   * いまの this.step を画面に反映する。
   *
   * ★ 副作用を持たせない ★
   * ここに通信や描画のやり直しを入れると、open() から呼んだときに
   * 開き直すたびに走ってしまう（STEP3 の /quote/confirm 再送など）。
   * ステップに紐づく処理は goTo() 側に置く。
   */
  syncStepView() {
    // ★ STEP1 の名前だけ出し分ける（2026-08-21）★
    //   STEP1 は「エリア確認」ではなく進め方を選ぶ画面。
    //   ステップ数は3つのままなので、ラベル1件だけ差し替える。
    const isChoice = this.step === 1;
    this.stepLabel.textContent = isChoice
      ? (this.choiceStepLabel || 'ご購入の前に')
      : STEP_LABELS[this.step];

    // ★ 進め方を選ぶ画面では進捗バーを出さない（2026-08-21）★
    //   まだ手続きが始まっていないため。レジに進むだけの人にも3手続きが
    //   必ずあるように見えてしまう。ラベルと同じ条件で出し入れする。
    if (this.progress) this.progress.hidden = isChoice;

    this.syncBackButton();

    this.querySelectorAll('[data-iq-step-panel]').forEach((panel) => {
      panel.hidden = Number(panel.dataset.iqStepPanel) !== this.step;
    });
    this.syncProgress();
  }

  /**
   * 通信中・シート表示中に「押させない」を掛ける。
   *
   * ★ 印（`data-iq-held`）を付ける（日高さん指摘 2026-08-28）★
   * カートへ入れたあとは `window.location.href` でカートへ移るので、この
   * `disabled` は**戻さないまま**ページを離れる。ブラウザの戻るで帰ってくると
   * bfcache が **DOM ごと復元する**ため、`disabled` が付いたままのボタンが出る。
   * ∴「カートに行って戻る」を繰り返すと**レジに進むボタンが押せなくなる**。
   *
   * 印を付けておき、`pageshow` でこの印の付いたものだけ戻す。印で選ぶのは、
   * 新規取り付けのときに恒久的に無効化しているボタン（`aria-disabled` を伴う方）
   * まで一緒に有効化してしまわないため。
   *
   * @param {HTMLButtonElement|null} el
   * @param {string} [busyLabel] 表示を差し替えるときの文言。元の文言は戻すときに復元する
   */
  holdButton(el, busyLabel) {
    if (!el) return;
    el.disabled = true;
    el.dataset.iqHeld = '1';
    if (typeof busyLabel === 'string') {
      el.dataset.iqLabel = el.textContent;
      el.textContent = busyLabel;
    }
  }

  /** `holdButton()` を解く。文言を差し替えていれば元に戻す */
  releaseButton(el) {
    if (!el) return;
    el.disabled = false;
    if (el.dataset.iqLabel !== undefined) {
      el.textContent = el.dataset.iqLabel;
      delete el.dataset.iqLabel;
    }
    delete el.dataset.iqHeld;
  }

  /**
   * 掛けたままの「押させない」を全部解く。
   *
   * ★ 戻るで帰ってきたときに呼ぶ ★
   * `bypassIntercept` を毎回落としているのと同じ理由（:494 のコメント）。
   * bfcache は JS の状態も DOM も復元するので、**離れる直前の状態がそのまま出る**。
   */
  releaseHeldButtons() {
    this.querySelectorAll('[data-iq-held]').forEach((el) => this.releaseButton(el));
  }

  /**
   * 戻るで帰ってきたときに、進行中のまま固まった状態を解く。
   *
   * ★ bfcache は「離れる直前」をそのまま戻す ★
   * DOM も JS のプロパティも復元される一方、**通信は切れている**。
   * ∴「途中だから触らせない」ために立てたものが、誰も倒さないまま残る。
   *
   *   `disabled` … カートへ移る直前に掛けたもの（`holdButton()`）
   *   `analyzing` … 解析中に立てたもの。解析の画面（`analyzingHtml`）と
   *                 戻るボタンの無効化がセットで残り、**「AIが写真を解析しています」
   *                 のまま二度と進まない**画面になる
   *
   * 解析は `cancelAnalyze()` で畳む。epoch を進めるので、万一あとから応答が
   * 届いても捨てられる。写真そのものは消さない（撮り直しをさせない）。
   */
  recoverFromRestore(restored) {
    // 掛かっているものが無ければ何も起きないので、こちらは毎回でよい
    this.releaseHeldButtons();

    /**
     * ★ 解析を畳むのは bfcache から戻ったときだけ ★
     * `pageshow` は通常の読み込みでも飛ぶ（Horizon は cross-document view transition
     * を使う。`assets/scroll-container.js:51`）。無条件に畳むと、**進行中の解析を
     * こちらから止めてしまう**経路を作ることになる。
     * `disabled` が残る不具合が起きるのは DOM ごと復元される bfcache のときだけで、
     * そのとき `persisted` は必ず true になる。
     */
    if (restored && this.analyzing === true) this.cancelAnalyze();
  }

  /**
   * 戻る。
   *
   * ★ STEP2 の中では「写真1枚ぶん」だけ戻る（日高さん指摘 2026-08-28）★
   * もとは無条件に `goTo(this.step - 1)` で、室外機を撮っている途中に押すと
   * **STEP1（郵便番号）まで一気に戻っていた**。撮り終えた写真は残るので
   * 「AI写真診断」を押し直せば途中まで復帰できるが、**戻るを押した人が期待するのは
   * 1つ前の写真**（室外機 → 室内機）であって最初の画面ではない。
   *
   * ★ 状態は何も捨てない ★
   * `goToShot()` は `shotIndex` を動かして描き直すだけで、写真・解析結果・
   * カバーの選択には触れない。既にドット（`data-iq-dot`）で同じ移動ができており、
   * そこと同じ道を通す。**捨てる処理を新しく足さない**のが、ここでの再発防止。
   *
   * ★ 解析中は動かさない ★
   * 表示条件ではなくハンドラ側で止める（表示条件に載せると、別画面から戻った
   * ときの再描画中にボタンが消える）。`syncBackButton()` の `disabled` と二重に
   * なるが、`disabled` は DOM が描き直されるまでの間に隙がある。
   */
  goBack() {
    if (this.analyzing === true) return;
    if (this.step === 2 && this.shotIndex > 0) {
      this.goToShot(this.shotIndex - 1);
      return;
    }
    this.goTo(this.step - 1);
  }

  /**
   * 戻るボタンの出し分け。
   *
   * 1ステップ目には戻り先が無い。解析中は**押させない**（戻ったあとに応答が
   * 届くと STEP3 へ強制的に飛ばされ、顧客の操作を奪う）。
   */
  syncBackButton() {
    if (!this.backButton) return;
    this.backButton.hidden = this.step === 1;
    this.backButton.disabled = this.analyzing === true;
  }

  /**
   * 進捗バーの塗り分け。
   *
   * ★ open() からも呼ぶ ★
   * goTo() の中だけで塗っていたため、開いた直後（step=1 のまま goTo を通らない）は
   * **3つとも未点灯**で、進捗が 0/3 に見えていた。STEP2 に進んだ瞬間に1と2が同時に
   * 点くという不自然な出方にもなっていた。
   */
  syncProgress() {
    this.querySelectorAll('[data-iq-seg]').forEach((seg) => {
      seg.classList.toggle('is-done', Number(seg.dataset.iqSeg) <= this.step);
    });
  }

  // ------------------------------------------------------------------
  // 見積セッション
  // ------------------------------------------------------------------

  /**
   * セッションを1回だけ発行する。
   *
   * ★ 二重に叩かない ★
   * 1回の呼び出しで Customer 1行 + Quote 1行が作られる。開くたび・連打のたびに
   * 増やさないよう、保存済みトークンと in-flight Promise の両方を見る。
   */
  ensureSession() {
    const saved = this.readSession();
    if (saved) {
      this.session = saved;
      // 先に始めていた見積を使い回すときは、診断の ID を後から付ける（待たない）
      this.linkDiagnosis(saved);
      return Promise.resolve(saved);
    }
    // ★ 解決済みの Promise を残さない ★
    //   readSession() が期限切れとして捨てたあとでも、成功した sessionPromise が
    //   残っていると**期限切れトークンを返し続ける**。発行が進行中のときだけ
    //   相乗りさせ、完了したら手放す。
    if (this.sessionPromise) return this.sessionPromise;

    this.sessionPromise = this.issueSession()
      .then((session) => {
        this.session = session;
        this.writeSession(session);
        this.sessionPromise = null;
        return session;
      })
      .catch((error) => {
        // 次に開いたときに再挑戦できるよう、失敗した Promise は残さない
        this.sessionPromise = null;
        this.showError('ただいま手続きを開始できません。時間をおいてお試しください。');
        throw error;
      });

    return this.sessionPromise;
  }

  /** 失効したセッションを捨てる。次の ensureSession() で発行し直される */
  forgetSession() {
    this.session = null;
    this.sessionPromise = null;
    try {
      sessionStorage.removeItem(storageKey(this.modelNo));
    } catch {
      // sessionStorage が使えない環境。メモリ側を捨てられていれば足りる
    }
    this.forgetPhotos();
  }

  /**
   * 撮った写真とその解析状態を捨てる。
   *
   * ★ セッションを捨てるときは写真も捨てる（2026-08-20）★
   * 解析の生データは **backend の `Quote.analysisSnapshot`** にある。見積セッションが
   * 張り替わると新しい Quote を指すので、そちらは空になる。それなのに画面側が
   * 「4枚とも認識できた」ままだと、
   *   - 「この写真で見積を確定する」が押せる（4枚そろって見える）
   *   - でも `/photos/finish` は 1枚も見つからず 409 を返す
   *   - 「写真だけでは判断できませんでした」に落ちる
   * という、写真が写っているのに判定できない画面になる。
   * 写真を消しておけば「あと4枚」に戻り、撮り直せば進める。
   *
   * ★ 世代も進める ★
   * 進行中の `/photos/shot` の応答が、捨てたあとに届いて復活するのを防ぐ。
   */
  forgetPhotos() {
    for (const kind of this.shotEpoch.keys()) {
      this.shotEpoch.set(kind, (this.shotEpoch.get(kind) || 0) + 1);
    }
    this.photos.clear();
    this.shotState.clear();
    this.shotRecog.clear();
    this.shotReason.clear();
    this.analysis = null;
    this.shotIndex = 0;
    // ★ カバーの判定と選択も捨てる ★
    //   判定は写真（塗装・破損）と型番（発売年）から出している。写真をやり直したのに
    //   前の判定が残ると、**別のカバーの状態で「再利用できます」と出す**ことになる。
    this.forgetCoverChoice();
    // ★ 回答も捨てる ★
    //   回答は金額（高所作業費・架台費）に効く。別の部屋・別の号機で
    //   やり直す人の古い回答が残ると、写真と噛み合わない額で確定してしまう。
    this.guardAnswers = {};
    this.guardAttempted = null;
    this.persistGuardAnswers();
    // 写真ステップを開いている最中なら、その場で「あと4枚」に戻す
    if (this.step === 2) this.renderPhotos();
  }

  /**
   * 使い回す見積に診断の見積IDを付ける。
   *
   * 商品ページの見積は sessionStorage に2時間残るので、同じタブで先に始めていた
   * お客様が比較ページから戻ってくると /quote/session が呼ばれず、ID を渡す機会が無い。
   *
   * ★ this.call を使わない ★
   *   call() は ensureSession() を呼び、ここへ戻ってきて二重に送る。
   *   トークンが切れていたら何もしない（作り直す見積には issueSession が ID を載せる）。
   * ★ 失敗しても見積は止めない ★ 記録のための値なので、結果は待たずに握りつぶす。
   */
  linkDiagnosis(session) {
    // ★ 同期部分も try で包む ★
    //   ensureSession() の中から呼ぶので、ここで投げると見積そのものが始められなくなる。
    //   非同期の失敗は末尾の .catch、同期の失敗（URL の組み立て等）はこの try で止める。
    try {
      const id = this.diagnosisQuoteId;
      if (!id || !session?.token || !session.quoteId) return;
      if (session.diagnosisQuoteId === id) return;
      const tried = `${session.quoteId}:${id}`;
      if (!this.diagnosisLinkTried || this.diagnosisLinkTried.has(tried)) return;
      this.diagnosisLinkTried.add(tried);

      const url = new URL(`${this.proxyBase}/quote/diagnosis-link`, window.location.origin);
      url.searchParams.set(TOKEN_QUERY_KEY, session.token);
      fetch(url.toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ diagnosisQuoteId: id }),
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => {
          if (!body?.success) return;
          // 次に開いたとき送り直さないよう、保存済みのセッションにも印を付ける
          const current = this.readSession();
          if (current?.quoteId === session.quoteId) {
            this.writeSession({ ...current, diagnosisQuoteId: id });
          }
        })
        .catch(() => {});
    } catch {
      // 記録のための値。付けられなくても見積は続ける
    }
  }

  /**
   * カートに診断の見積IDを載せる（2026-10-18）。カートに入れた**あと**に呼ぶ。
   *
   * ★ 入れたあとに書く ★
   *   空のカートに属性を書いても残る保証は公式に無い（assets/aicon-return.js と同じ理由）。
   * ★ 無いときは書かない（空文字で消さない）★
   *   カート属性はカートに1つ。診断から来た1台目のあとに、診断を通らず2台目を入れた
   *   ときに消すと、1台目が診断から来たことまで消える。
   * ★ 失敗しても購入は止めない ★ 記録のための値。結果を待つのはカートへ移る前だけ。
   */
  async tagCartWithDiagnosis() {
    const id = this.diagnosisQuoteId;
    if (!id) return;
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [DIAGNOSIS_CART_ATTR]: id } }),
      });
    } catch {
      // 載せられなくてもカートには入っている。そのまま進む
    }
  }

  async issueSession() {
    const res = await fetch(`${this.proxyBase}/quote/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // diagnosisQuoteId は比較ページ経由で診断から来たときだけ。無ければ今までどおり
      body: JSON.stringify(
        this.diagnosisQuoteId
          ? { modelNumber: this.modelNo, diagnosisQuoteId: this.diagnosisQuoteId }
          : { modelNumber: this.modelNo },
      ),
    });
    const body = await res.json().catch(() => null);

    if (!res.ok || !body?.success) {
      throw new Error(body?.error?.code || `session_failed_${res.status}`);
    }
    return body.data;
  }

  /** 期限切れのトークンを使い回さない。切れていれば捨てて発行し直す */
  readSession() {
    try {
      const raw = sessionStorage.getItem(storageKey(this.modelNo));
      if (!raw) return null;

      const session = JSON.parse(raw);
      if (!session?.token || !session?.expiresAt) return null;
      if (new Date(session.expiresAt).getTime() <= Date.now()) {
        sessionStorage.removeItem(storageKey(this.modelNo));
        return null;
      }
      return session;
    } catch {
      // プライベートブラウジング等で sessionStorage が使えないことがある。
      // その場合は毎回発行する（動かなくなるよりは良い）
      return null;
    }
  }

  writeSession(session) {
    try {
      sessionStorage.setItem(storageKey(this.modelNo), JSON.stringify(session));
    } catch {
      // 保存できなくても進行はできる
    }
  }

  // ------------------------------------------------------------------
  // API 呼び出しの共通部分（TASK-0011 以降が使う）
  // ------------------------------------------------------------------

  /**
   * App Proxy 経由で叩く。トークンは**クエリ**に載せる。
   *
   * @param {string} path  `/photos` のような proxyBase からの相対パス
   * @param {{ method?: string, body?: unknown, params?: Record<string,string> }} options
   */
  async call(path, options = {}) {
    const session = await this.ensureSession();
    const url = new URL(`${this.proxyBase}${path}`, window.location.origin);

    Object.entries(options.params || {}).forEach(([k, v]) => url.searchParams.set(k, v));
    url.searchParams.set(TOKEN_QUERY_KEY, session.token);

    const res = await fetch(url.toString(), {
      method: options.method || 'GET',
      headers: options.body ? { 'Content-Type': 'application/json' } : undefined,
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
    const body = await res.json().catch(() => null);

    if (!res.ok || !body?.success) {
      // ★ トークンが失効していたら1回だけ発行し直して再試行する ★
      //   セッションの TTL は2時間。モーダルを開いたまま放置した顧客が
      //   ここで行き止まりにならないようにする。無限ループを避けるため1回だけ。
      if (body?.error?.code === 'QUOTE_SESSION_INVALID' && !options.retried) {
        this.forgetSession();
        return this.call(path, { ...options, retried: true });
      }

      const error = new Error(body?.error?.message || '通信に失敗しました');
      error.code = body?.error?.code;
      error.nextAction = body?.error?.nextAction;
      throw error;
    }
    return body.data;
  }

  /**
   * 写真判定の可否表示を初期化する。
   *
   * ★ 開くたびに評価し直す ★
   * 購入方法のラジオは**モーダルの外**にあり、オーバーレイ（inset:0 / z-index:100）が
   * クリックを吸うため、切り替えは必ず「閉じてから」起きる。
   * close() も open() も DOM を作り直さないので、ここで戻さないと
   * 「新規で一度押して無効化 → 閉じて取替えに変更 → 再度開く」で
   * **取替えなのに写真判定へ進めない**状態が残る（再読み込みするまで直らない）。
   */
  resetPhotoAvailability() {
    // ★ 走査範囲は STEP1 パネル全体（2026-08-21）★
    //   写真へ進むボタン（liquid が静的に置く [data-iq-choose-photos]）を
    //   無効化したあと戻せず、「新規で一度押す → 閉じて取替えに変える → 開き直す」で
    //   押せないままだった不具合の対策。
    const scope = this.step1Panel || this;

    scope.querySelector('[data-iq-photos-unavailable]')?.remove();

    scope.querySelectorAll('[data-iq-choose-photos]').forEach((button) => {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
    });

    this.syncChooseView();
  }

  /**
   * 「進め方を選ぶ」画面（郵便番号を出さないとき）を購入方法に合わせる。
   *
   * ★ 新規取り付けでは診断の一式を出さない（日高さん指示 2026-08-24）★
   * この画面は liquid が静的に出しているため、購入方法のラジオ（モーダルの外）を
   * 見た出し分けができない。以前は診断ボタンを出したまま押されてから
   * showPhotoUnavailable() で断っていたが、**取れない割引（◯◯円引き）まで
   * 一緒に見せていた**。
   *
   * 出し分けるのは3つ。
   *   1. 診断の一式（一言 → ボタン → 割引の条件）を hidden にする
   *   2. 注記を「診断は任意」から「新規は対象外」に差し替える
   *   3. 「レジに進む」を枠線から塗りに上げる（唯一の進路になるため）
   *
   * ★ 開くたびに戻す ★
   * 「新規で開く → 閉じて取替えに変える → 開き直す」で診断が出ないままにならないよう、
   * どちらの向きにも必ず書き戻す（resetPhotoAvailability から呼ばれる）。
   */
  syncChooseView() {
    const scope = this.step1Panel || this;
    const group = scope.querySelector('[data-iq-choose-photos-group]');
    if (!group) return;

    const isNew = !this.canUsePhotos();

    group.hidden = isNew;

    // ★ 新規のときは「診断は任意です」を出さない ★
    //   診断UIが無い画面に「※AI写真診断は任意です。診断なしでご購入の場合…」が
    //   残ると、出ていないものについて書いていることになる。
    //   差し替え先（choose_new_note）が設定で空にされているときは、
    //   liquid が要素そのものを出さないので**どちらも出ない**。それでよい。
    const note = scope.querySelector('[data-iq-choose-note]');
    const newNote = scope.querySelector('[data-iq-choose-new-note]');
    if (note) note.hidden = isNew;
    if (newNote) newNote.hidden = !isNew;

    // ★ 進路が1本になったら枠線ではなく塗りにする ★
    //   枠線ボタンは「診断させないための出口」として弱く見せている見た目なので、
    //   唯一の進路になった画面でそのまま残すと押す先が無い画面に見える。
    scope
      .querySelector('[data-iq-choose-skip]')
      ?.classList.toggle('aicon-iq__outline--only', isNew);
  }

  /**
   * 新規取り付けで写真判定が使えないことを、その場に出す。
   *
   * ボタンは残したまま無効化し、直後に理由を差し込む。
   * 「写真なしで進む」は塞がないので、行き止まりにはならない。
   *
   * ★ 通常の経路では呼ばれない（2026-08-24）★
   * 新規取り付けでは、写真へ進むボタン（[data-iq-choose-photos]）は
   * syncChooseView が器ごと hidden にするので**そもそも出ない**。
   * それでも消していないのは、押されたときの最後の受け皿として残すため。
   * 出し分けが将来壊れたとき、ここが無いと「押せるのに何も起きない」に戻る。
   * 呼び出し側のガード（connectedCallback）も同じ理由で残してある。
   */
  showPhotoUnavailable(button) {
    const scope = this.step1Panel || this;
    if (!scope) return;

    const existing = scope.querySelector('[data-iq-photos-unavailable]');
    if (existing) {
      existing.focus?.();
      return;
    }

    const note = document.createElement('p');
    note.className = 'aicon-iq__unavailable';
    note.setAttribute('data-iq-photos-unavailable', '');
    note.setAttribute('role', 'status');
    note.setAttribute('tabindex', '-1');
    note.textContent =
      '新規取り付けは写真での確認の対象外になります。'
      + 'いま付いているエアコンと周りの様子を見て判断する仕組みのため、'
      + '既存の機種が無い場合は写真から追加工事を出せません。'
      + 'このまま「写真なしで進む」を選んでください。担当者が現地で確認します。';

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.after(note);
    } else {
      scope.append(note);
    }
    note.focus();
  }

  /** API の値をそのまま innerHTML に入れないための最小限のエスケープ */
  /**
   * 撮影の注意書きを HTML にする。文字列ならそのまま、配列なら `{ b }` だけ太字にする。
   * どちらも escape してから組むので、文言にタグが混ざっても効かない。
   */
  noteHtml(note) {
    const parts = Array.isArray(note) ? note : [note];
    return parts
      .map((part) => (part && typeof part === 'object' ? `<b>${this.escape(part.b)}</b>` : this.escape(part)))
      .join('');
  }

  escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[c]);
  }

  // ------------------------------------------------------------------
  // STEP 2: 写真
  // ------------------------------------------------------------------

  /** 4枠を描く。撮れているものはサムネイル、まだのものは追加ボタン */
  renderPhotos() {
    if (!this.photosRoot) return;

    const shot = SHOTS[Math.min(this.shotIndex, SHOTS.length - 1)];
    const shotImages = this.photos.get(shot.kind) || [];
    const picked = shotImages[0];
    const state = this.shotState.get(shot.kind) || 'idle';
    const rows = this.shotRecog.get(shot.kind) || [];
    /**
     * ★ 「撮った枚数」ではなく「解析が終わった種類の数」で数える ★
     * 複数枚モードでは撮っただけで解析していない状態（pending）がある。撮影枚数で
     * 数えると、**貯めている途中なのに「見積を確定する」が出てしまい、追加工事が
     * 反映されない金額で確定できる**。判定が済んだものだけを数える。
     */
    const remaining = SHOTS.length - this.analyzedCount();

    this.photosRoot.innerHTML = `
      <h3 class="aicon-iq__shotTitle">${this.escape(shot.title)}を撮影</h3>

      ${
        this.airoSrc
          ? `<div class="aicon-iq__say">
               <img class="aicon-iq__airo" src="${this.escape(this.airoSrc)}" alt="" width="34" height="34" loading="lazy">
               <p class="aicon-iq__bubble">${this.escape(shot.why)}</p>
             </div>`
          : `<p class="aicon-iq__lead">${this.escape(shot.why)}</p>`
      }

      <div class="aicon-iq__stage${picked ? ' is-done' : ''}">
        <span class="aicon-iq__corner tl" aria-hidden="true"></span>
        <span class="aicon-iq__corner tr" aria-hidden="true"></span>
        <span class="aicon-iq__corner bl" aria-hidden="true"></span>
        <span class="aicon-iq__corner br" aria-hidden="true"></span>
        ${
          picked
            ? `<img class="aicon-iq__stageShot" src="${picked.dataUrl}" alt="撮影した${this.escape(shot.title)}">
               ${state === 'analyzing' ? '<span class="aicon-iq__scanline" aria-hidden="true"></span>' : ''}`
            : `<span class="aicon-iq__stageHint">${this.escape(shot.hint)}</span>`
        }
      </div>

      ${this.multiHtml(shot, shotImages, state)}

      ${
        // 解析中は診断SPA と同じ「AIが写真を解析しています…」を出す
        state === 'analyzing'
          ? `<p class="aicon-iq__scanning" role="status">
               <span class="aicon-iq__spinner" aria-hidden="true"></span>AIが写真を解析しています…
             </p>`
          : ''
      }

      ${
        // 認識できた項目を1行ずつ。診断SPA と同じ文言・同じ順番（サーバーが組み立てる）
        state === 'done' && rows.length > 0
          ? `<div class="aicon-iq__recog">
               ${rows
                 .map(
                   (r) => `<p class="aicon-iq__recogRow">
                     <span class="aicon-iq__recogCheck" aria-hidden="true">✓</span>
                     <span class="aicon-iq__recogLabel">${this.escape(r.label)}</span>
                     <b class="aicon-iq__recogValue">${this.escape(r.value)}</b>
                   </p>`,
                 )
                 .join('')}
             </div>`
          : ''
      }

      ${
        // ★ この写真に添える注意書き（2026-08-28）★
        //   認識結果の下、次へ進むボタンの上に置く。読んでから進める位置。
        //   撮る前ではなく撮った後に出すのは、撮影の手順を増やして迷わせないため。
        state === 'done' && shot.note
          ? `<p class="aicon-iq__shotNote">${this.noteHtml(shot.note)}</p>`
          : ''
      }

      ${
        // 撮り直しをお願いする理由。「失敗しました」で終わらせない
        state === 'retake'
          ? `<p class="aicon-iq__unavailable" role="status">${this.escape(
              this.shotReason.get(shot.kind) || '写真を確認できませんでした。撮り直してください。',
            )}</p>`
          : ''
      }

      <div class="aicon-iq__shotActions">
        ${
          // ★ 1枚だけとばす動線は置かない（日高さん指摘 2026-08-20）★
          //   診断SPA には元から無く、あちらのコードにも「スキップ動線は廃止」と明記されている
          //   （PhotoView.tsx）。4枚そろって初めて判定できるので行き先が無い。
          state === 'done'
            ? `${
                this.shotIndex < SHOTS.length - 1
                  ? '<button type="button" class="aicon-iq__cta" data-iq-next-shot>次の写真へ ›</button>'
                  : ''
              }
              <button type="button" class="aicon-iq__link" data-iq-retake>この写真を撮り直す</button>`
            : state === 'analyzing'
              ? '<button type="button" class="aicon-iq__cta" disabled>撮影する</button>'
              : // 複数枚を貯めている途中。撮り終えたら本人が解析を始める
                state === 'pending'
                ? `<button type="button" class="aicon-iq__cta" data-iq-analyze-shot>この${shotImages.length}枚で解析する ›</button>
                   <button type="button" class="aicon-iq__link" data-iq-retake>撮り直す（全部消す）</button>`
                : `<button type="button" class="aicon-iq__cta" data-iq-open-guide>撮影する</button>`
        }
      </div>

      ${this.dotsHtml()}

      ${
        // 4枚そろったら次のステップへ。判定は1枚ずつ済んでいるので、ここは確定だけ
        remaining === 0
          ? '<button type="button" class="aicon-iq__cta" data-iq-analyze>この写真で見積を確定する ›</button>'
          : `<p class="aicon-iq__remaining">あと${remaining}枚で見積を確定できます</p>`
      }
      <button type="button" class="aicon-iq__ghost" data-iq-skip-analyze>写真なしで進む</button>

      ${this.guideSheetHtml(shot)}
      ${this.coverSheetHtml()}

      <input type="file" accept="image/*" class="aicon-iq__hiddenFile" data-iq-file="${shot.kind}">
    `;

    this.bindPhotoHandlers(shot);
  }

  /**
   * 外化粧カバーの仕上げを選ぶシート。
   *
   * ★ web版 CoverView をそのまま持ってきている ★
   * 選択肢の文言・並び・色の一覧・「再利用は既存カバーがある場合のみ出す」まで
   * `frontend/src/components/diagnosis/views/CoverView.tsx` と同じ。
   * 片方だけ変えると、同じお客様が経路によって違う選択肢を見ることになる。
   *
   * ★ 判定はサーバから引く ★
   * 「再利用できるか」は AI の判定（塗装・破損）に**型番の発売年**を重ねて決まる。
   * テーマ側で計算できないので `/photos/cover` に聞く。確定時にもサーバがもう一度
   * 引き直して照合するので、ここでの表示が改ざんされても金額は動かない。
   */
  coverSheetHtml() {
    const ctx = this.coverContext;
    if (!ctx) return '';
    const plan = this.coverPlan;
    const needColor = plan === 'new';
    // 架台の選択肢を出すときは、見出しにも架台を入れる（配管カバーだけの画面に見せない）
    const hasStand = this.standContext?.present === true;
    const sheetTitle = hasStand ? '架台と配管カバーの確認' : '配管カバーの仕上げ';

    const opt = (value, label) =>
      `<button type="button" class="aicon-iq__coverOpt${plan === value ? ' is-sel' : ''}"
         data-iq-cover-plan="${value}">${label}</button>`;

    return `
      <div class="aicon-iq__coverBack" data-iq-cover-back hidden></div>
      <section class="aicon-iq__coverSheet" data-iq-cover-sheet hidden
        role="dialog" aria-modal="true" aria-label="${sheetTitle}" tabindex="-1">
        <h3 class="aicon-iq__shotTitle">${sheetTitle}</h3>
        ${
          this.airoSrc
            ? `<div class="aicon-iq__say">
                 <img class="aicon-iq__airo" src="${this.escape(this.airoSrc)}" alt="" width="34" height="34" loading="lazy">
                 <p class="aicon-iq__bubble">${
                   hasStand
                     ? '写真から架台と配管カバーの状態を確認しました。<b>再利用できるか / 交換するか</b>で費用が変わるため、お選びください。'
                     : '写真から配管カバーの状態を確認しました。<b>再利用できるか / 新品に交換するか</b>で費用が変わるため、お選びください。'
                 }</p>
               </div>`
            : ''
        }

        ${this.standSectionHtml()}

        ${this.indoorSectionHtml()}

        <p class="aicon-iq__coverLabel">室外の配管カバー</p>
        <div class="aicon-iq__coverAi${ctx.canReuse === true ? '' : ' is-info'}">
          <span aria-hidden="true">${ctx.canReuse === true ? '✓' : 'ⓘ'}</span>
          <span>AI判定：${this.escape(ctx.reason || '')}</span>
        </div>

        <div class="aicon-iq__coverOpts">
          ${
            /**
             * ★ テープ巻きと新品は必ず出す ★
             * 仕様（PDF 2026-05-19 / `frontend/src/lib/cover-helpers.ts`）では、
             * 再利用できないときは**「テープ巻き / 配管カバー新品」の2択**になる。
             * 再利用のボタンを**消す**のであって、他の選択肢まで塞がない。
             * 塞ぐと、テープ巻きで足りる人にも新品代（5,500円）を求めることになる。
             */ ''
          }
          ${opt('none', 'テープ巻き（標準仕上げ・+0円）')}
          ${opt('new', '配管カバー新品')}
          ${
            // 再利用は「状態が良い既存カバーがある」ときだけ。判定はサーバが出す
            ctx.canReuse === true ? opt('reuse', '既存カバーを再利用する') : ''
          }
        </div>

        ${
          needColor
            ? `<p class="aicon-iq__coverLabel">カバーの色</p>
               <div class="aicon-iq__coverColors">
                 ${COVER_COLORS.map(
                   (c) => `<button type="button" class="aicon-iq__coverColor${
                     this.coverColor === c.name ? ' is-sel' : ''
                   }" data-iq-cover-color="${this.escape(c.name)}">
                     <span class="aicon-iq__coverChip" style="background:${c.chip}"></span>
                     ${this.escape(c.name)}
                   </button>`,
                 ).join('')}
               </div>`
            : ''
        }

        <button type="button" class="aicon-iq__cta" data-iq-cover-done${
          this.canFinishCover() ? '' : ' disabled'
        }>この内容で見積を確定する ›</button>
      </section>
    `;
  }

  /**
   * カバー選択のシートを開く。判定はここで初めて引く。
   *
   * ★ 引けなくても止めない ★
   * 判定が出せなくても、お客様が見て選ぶことはできる。「カバーは無い」前提で
   * 開き、新しく付ける / テープ巻き から選んでもらう。ここで止めると
   * **写真を撮り終えた人が確定できなくなる**。
   */
  async openCoverSheet() {
    if (this.analyzedCount() !== SHOTS.length) return;
    // 確認中の二度押しで、同じ問い合わせを重ねない
    if (this.coverLoading) return;

    if (!this.coverContext || !this.standContext) {
      /**
       * ★ 待つ間は「確認中」と出す（#1117 / 2026-10-08）★
       *   `/photos/cover` は機種の発売年がまだ取れていなければ、最大十数秒待ってから返す
       *   （配管カバーを再利用できるかが発売年で決まるため）。押しても何も起きないように
       *   見えると、もう一度押されるか離脱される。
       */
      this.coverLoading = true;
      const button = this.photosRoot?.querySelector('[data-iq-analyze]');
      const label = button?.textContent;
      if (button) {
        button.disabled = true;
        button.textContent = 'ご使用中の機種の情報を確認しています…';
      }
      try {
        // 架台の選択肢は撮影前の回答（架台の有無・種類）にもよるので、一緒に送る
        const answers = this.guardAnswers || {};
        const params = {};
        if (answers.standHas) params.standHas = answers.standHas;
        if (answers.standType) params.standType = answers.standType;
        const result = await this.call('/photos/cover', { method: 'GET', params });
        this.coverContext = result?.outdoor || null;
        this.indoorContext = result?.indoor || null;
        this.standContext = result?.stand || null;
      } catch (error) {
        console.warn('[aicon] カバー判定を引けませんでした', error);
      } finally {
        this.coverLoading = false;
        if (button) {
          button.disabled = false;
          button.textContent = label;
        }
      }
      /**
       * 判定を引けなかったときの既定値。
       *
       * ★ どれも初期選択しない ★
       * 以前は「再利用できないなら新品を選んでおく」としていたが、テープ巻きも
       * 選べる以上、**どちらを選ぶかはお客様が決めること**。先に新品を入れておくと、
       * そのまま確定して 5,500円 を払うことになる。
       */
      if (!this.coverContext) {
        this.coverContext = {
          canReuse: false,
          reason: '既存の配管カバーはありません（新しく付けるか、テープ巻き仕上げをお選びください）',
        };
      }
      // 室内は「選択肢を出さない」で倒す。室内カバー費は計上されない。
      if (!this.indoorContext) {
        this.indoorContext = { present: false, canReuse: false, reason: '' };
      }
      // 架台は「出さない」で倒す。確定時にサーバが判定し直し、架台があれば
      // 「再利用できるなら再利用・できなければ交換」で計上する（`resolveStandPlan`）
      if (!this.standContext) this.standContext = { present: false };

      /**
       * ★ 判定を取り直したら、出せなくなった選択を捨てる（#1327 と同じ理由）★
       *   架台の回答を変えると判定を取り直す。その間に発売年が取れて「再利用できない」に
       *   変わると、前に選んだ「再利用」が**ボタンは消えたのに選択だけ残る**。
       *   金額はサーバが直すが、お客様からは選んでいないのに確定できるように見える。
       */
      if (this.coverPlan === 'reuse' && this.coverContext?.canReuse !== true) this.coverPlan = null;
      if (this.interiorPlan === 'reuse' && this.indoorContext?.canReuse !== true) this.interiorPlan = null;
      if (this.standPlan === 'reuse' && this.standContext?.canReuse !== true) this.standPlan = null;
      if (this.standPlan === 'none' && this.standContext?.canDecline !== true) this.standPlan = null;
    }

    this.renderPhotos();
    const back = this.photosRoot?.querySelector('[data-iq-cover-back]');
    const sheet = this.photosRoot?.querySelector('[data-iq-cover-sheet]');
    if (back) back.hidden = false;
    if (sheet) {
      sheet.hidden = false;
      sheet.focus?.();
    }
  }

  /** シートを開いたまま描き直す（選択の反映で閉じてしまわないように） */
  rerenderCoverSheet() {
    this.renderPhotos();
    const back = this.photosRoot?.querySelector('[data-iq-cover-back]');
    const sheet = this.photosRoot?.querySelector('[data-iq-cover-sheet]');
    if (back) back.hidden = false;
    if (sheet) sheet.hidden = false;
  }

  pickCoverPlan(plan) {
    if (!plan) return;
    this.coverPlan = plan;
    // 新品以外は色を持たない（web版 CoverView と同じ）
    if (plan !== 'new') this.coverColor = null;
    this.rerenderCoverSheet();
  }

  pickInteriorPlan(plan) {
    if (!plan) return;
    this.interiorPlan = plan;
    this.rerenderCoverSheet();
  }

  pickCoverColor(color) {
    if (!color) return;
    this.coverColor = color;
    this.rerenderCoverSheet();
  }

  /**
   * 室内の配管カバー。
   *
   * ★ 室外と同じ3択（本流 `ai-consultation/page.tsx:4509-4520` と同じ）★
   *   テープ巻き（+0円） / 室内カバー新品 / （状態が良ければ）室内カバー再利用
   * 再利用だけを出し分ける。12年以上前の機種では消えて2択になる。
   *
   * ★ 室内配管の解析が走っていない部屋では section ごと出さない ★
   * 配管が見えない部屋には室内カバーの選択自体が無い。`canReuse` は
   * 「再利用ボタンを出すか」でしかないので、それだけでは判断できない。
   * サーバが返す `present`（＝室内にカバーが写っていたか）で出し分ける。
   */
  indoorSectionHtml() {
    const ctx = this.indoorContext;
    if (!ctx || ctx.present !== true) return '';

    const opt = (value, label) =>
      `<button type="button" class="aicon-iq__coverOpt${
        this.interiorPlan === value ? ' is-sel' : ''
      }" data-iq-interior-plan="${value}">${label}</button>`;

    return `
      <p class="aicon-iq__coverLabel">室内の配管カバー</p>
      <div class="aicon-iq__coverAi${ctx.canReuse === true ? '' : ' is-info'}">
        <span aria-hidden="true">${ctx.canReuse === true ? '✓' : 'ⓘ'}</span>
        <span>AI判定：${this.escape(ctx.reason || '')}</span>
      </div>
      <div class="aicon-iq__coverOpts">
        ${opt('none', 'テープ巻き（標準仕上げ・+0円）')}
        ${opt('new', '室内カバー新品')}
        ${ctx.canReuse === true ? opt('reuse', '室内カバー再利用') : ''}
      </div>
    `;
  }

  /**
   * 室外機の架台（#1117 / 2026-10-08）。
   *
   * ★ web版 AiCON にあった選択を戻した ★
   *   LIFF 版（`ai-consultation/page.tsx` の「架台の状態確認」）は、架台がある人に
   *   「再利用できる（+5,500円）/ 錆・破損あり（交換）（+13,200円）」を選ばせていた。
   *   EC に移したときにこの選択が抜け、架台費は**選ばせずに「新規設置」**で付いていた。
   *   EC の写真診断は取替え工事だけなので「新規」は出さない。文言と金額の添え方は LIFF に揃える。
   *
   * ★ 出すかどうかはサーバが決める ★
   *   お客様が「架台あり」と答えたか、AI が写真で架台を見つけたら出す（`buildStandContext`）。
   *   12年を超えた機種・錆や破損が写っていた架台は、再利用のボタンを出さない（交換のみ）。
   */
  standSectionHtml() {
    const ctx = this.standContext;
    if (!ctx || ctx.present !== true) return '';

    const yen = (n) => (Number.isFinite(Number(n)) ? `+${Number(n).toLocaleString('ja-JP')}円` : '');
    const opt = (value, label, fee) =>
      `<button type="button" class="aicon-iq__coverOpt${this.standPlan === value ? ' is-sel' : ''}"
         data-iq-stand-plan="${value}">${label}${fee ? `（${fee}）` : ''}</button>`;

    return `
      <p class="aicon-iq__coverLabel">室外機の架台（${this.escape(ctx.typeLabel || '')}）</p>
      <div class="aicon-iq__coverAi${ctx.canReuse === true ? '' : ' is-info'}">
        <span aria-hidden="true">${ctx.canReuse === true ? '✓' : 'ⓘ'}</span>
        <span>AI判定：${
          // 「ない」と答えた人には、AI が見つけたことを先に伝える（なぜ聞かれているのか分かるように）
          ctx.canDecline === true ? '写真に架台が写っているようです。' : ''
        }${this.escape(ctx.reason || '')}</span>
      </div>
      <div class="aicon-iq__coverOpts">
        ${ctx.canReuse === true ? opt('reuse', '再利用できる', yen(ctx.fees?.reuse)) : ''}
        ${
          // 再利用を選べないとき（12年超など）に「錆・破損あり」と書くと、理由と食い違う
          opt('replace', ctx.canReuse === true ? '錆・破損あり（交換）' : '架台を交換する', yen(ctx.fees?.replace))
        }
        ${
          /**
           * ★ 「架台はない」（日高さん決定 2026-10-08・案A）★
           *   撮影前に「ない」と答え、AI だけが「ある」と見たときに限って出す。
           *   AI の見間違いで、架台の無いお客様に架台費を必ず付けることになるのを防ぐ。
           */
          ctx.canDecline === true ? opt('none', '架台はない', '+0円') : ''
        }
      </div>
    `;
  }

  pickStandPlan(plan) {
    if (plan !== 'reuse' && plan !== 'replace' && plan !== 'none') return;
    // 「架台はない」は出しているときだけ選べる（サーバも canDecline で照合する）
    if (plan === 'none' && this.standContext?.canDecline !== true) return;
    this.standPlan = plan;
    this.rerenderCoverSheet();
  }

  /**
   * 選び終わったか。
   *   室外 … 必ず選ぶ。新品なら色まで選んで初めて確定できる
   *   室内 … **選択肢を出している部屋だけ**必須
   *   架台 … **選択肢を出しているときだけ**必須
   */
  canFinishCover() {
    if (!this.coverPlan) return false;
    if (this.coverPlan === 'new' && !this.coverColor) return false;
    if (this.indoorContext?.present === true && !this.interiorPlan) return false;
    if (this.standContext?.present === true && !this.standPlan) return false;
    return true;
  }

  /** 判定まで終わった種類の数（撮っただけの pending は数えない） */
  analyzedCount() {
    return SHOTS.filter((s2) => this.shotState.get(s2.kind) === 'done').length;
  }

  /**
   * その写真を複数枚で送れるか。
   *
   * ★ 撮る前の設問の答えで決まる ★
   * 室外機は「室外機と配管の接続部を、1枚の写真に写せますか？」に「写しきれない」と
   * 答えた人だけが追加できる。1枚で収まる人に追加を促すと、**同じ写真を何枚も撮らせて
   * AI クォータ（REQ-405）を無駄に減らす**。
   *
   * 上限5枚は web版（PhotoView.tsx の追加撮影バッファ）と同じ。
   * backend の `MAX_IMAGES_PER_KIND` と揃える（ずれると送ってから弾かれる）。
   */
  maxImagesFor(kind) {
    const answers = this.guardAnswers || {};
    if (kind === 'outdoor' && answers.outdoorOneShot === 'no') return 5;
    // ★ 室内機も追加できるようにした（日高さん指摘 2026-08-28）★
    //   backend は室内機を 5 枚受ける（`MAX_IMAGES_PER_KIND.indoor = 5`・#1329）のに、
    //   ここが 1 を返していたため**テーマに追加の口が出ていなかった**。
    //   しかも設問 `pipeAllInPhoto` に「一部隠れている」と答えた人へは
    //   noteWhen で「複数枚アップロードできます」と**案内済み**で、
    //   案内した動作ができない状態だった。
    //
    //   条件は室外機と同じ考え方で、「1枚に収まらない」と答えた人だけに出す。
    //   全部写せる人に追加を促すと、同じ写真を何枚も撮らせて AI クォータを削る。
    //   2枚目以降は `analyzeIndoorPipeMulti` へ回り、室内カバーの判定もそこで出る。
    if (kind === 'indoor' && answers.pipeAllInPhoto === 'no') return 5;
    return 1;
  }

  /**
   * 追加撮影の枚数ぶんのサムネイルと「もう1枚追加」。
   *
   * ★ 全枚数を横に並べる ★
   * web版で「複数枚アップロードしたのに1枚しか出ない」という指摘（FB 2026-07-30）が
   * あり、あちらは横並びで全部出す形に直っている。同じ作りにする。
   */
  multiHtml(shot, images, state) {
    const max = this.maxImagesFor(shot.kind);
    if (max <= 1) return '';
    /**
     * ★ 解析中は足させない・消させない（#1156・設計書 165-8）★
     *   解析中に「もう1枚追加する」で足すと［この◯枚で解析する］に戻り、押すと解析中の写真ごと
     *   送り直される。2つの解析が同時に走り、あとから終わった古い方の結果で見積が上書きされた
     *   （画面は新しい結果・請求は古い結果）。解析が終わってから足せば、全部をもう一度解析するだけで重ならない。
     */
    const analyzing = state === 'analyzing';
    return `
      <div class="aicon-iq__multi">
        <p class="aicon-iq__multiCount">追加した写真：${images.length} / ${max} 枚</p>
        <div class="aicon-iq__multiThumbs">
          ${images
            .map(
              (one, i) => `<span class="aicon-iq__multiThumb">
                 <img src="${one.dataUrl}" alt="追加した写真 ${i + 1}枚目">
                 ${
                   analyzing
                     ? ''
                     : `<button type="button" class="aicon-iq__multiDrop" data-iq-drop-image="${i}"
                   aria-label="${i + 1}枚目を削除">×</button>`
                 }
               </span>`,
            )
            .join('')}
        </div>
        ${
          analyzing
            ? '<p class="aicon-iq__multiFull">解析が終わってから写真を追加できます</p>'
            : images.length < max
              ? '<button type="button" class="aicon-iq__link" data-iq-add-image>もう1枚追加する</button>'
              : `<p class="aicon-iq__multiFull">上限の${max}枚に達しました</p>`
        }
      </div>
    `;
  }

  /**
   * 何枚目かのドット。
   *
   * ★ 先へは飛べない ★
   * 「とばす」ボタンを消しても、ドットを押せば未撮影のまま先の写真へ行けてしまう。
   * 同じ抜け道なので一緒に塞ぐ（診断SPA のドットは <i> で押せない）。
   * ただし**撮り終えた写真へ戻るのは許す**。撮り直したいときに戻れないと直せなくなる。
   */
  dotsHtml() {
    return `
      <div class="aicon-iq__dots" role="tablist" aria-label="写真の進み具合">
        ${SHOTS.map((s2, i) => {
          const done = this.photos.has(s2.kind);
          const canGo = done || i <= this.shotIndex;
          return `<button type="button" role="tab" class="aicon-iq__dot${
            i === this.shotIndex ? ' is-current' : ''
          }${done ? ' is-done' : ''}" data-iq-dot="${i}"${canGo ? '' : ' disabled'}
            aria-selected="${i === this.shotIndex}" aria-label="${i + 1}枚目 ${this.escape(s2.title)}"></button>`;
        }).join('')}
      </div>
    `;
  }

  /**
   * 撮影イメージのボトムシート。
   *
   * ★ 「撮影する」を押したら必ず出す（日高さん指示 2026-08-20）★
   * 既存の AiCON は撮影の直前に必ずこのシートを見せてから、カメラ／ライブラリを開く。
   * 折りたたみ（details）で「開けば見られる」状態だと、ほとんどの人が読まずに撮る。
   *
   * ★ 画像は web 側の /public/guide を辿る ★
   * テーマの assets に複製すると、片方だけ差し替えられて食い違う。
   * base が取れないときは画像なしで文言だけ出す（欠けたアイコンを出さない）。
   */
  /**
   * 撮る前に伺う質問（ガード）。web版 AiCON の PhotoView と同じ設問・同じ順番。
   *
   * ★ 回答は金額に効く ★
   *   階数と架台は backend の applyGuardAnswers が findings に重ねる。
   *   ここで聞かずに撮らせると、写真からの推定だけで高所作業費・架台費が決まる。
   *
   * ★ 表示条件つきの質問がある ★
   *   `when` を満たさない質問は出さない。出ていない質問は未回答として数えない
   *   （「見えない」を選んだ人に「全部写せますか」を求めない）。
   *   値は配列でも書ける（`{ floor: ['2階', '3階以上'] }` ＝ どれかに一致）。
   *
   * ★ 2階→1階を申告した人には階数・ベランダを聞かない（2026-09-15）★
   *   `hideWhenDownflow` の質問は出さない。申告した時点で「室内機2階・室外機1階」が
   *   確定しているので聞く意味がなく、**しかも正しく「1階」と答えられると
   *   高所作業費が消えていた**（#1049）。要否は backend が申告から確定させる
   *   （`/photos/finish` に `downflow` を送る → `applyGuardAnswers`）。
   */
  visibleGuards(shot) {
    const answers = this.guardAnswers || {};
    const downflow = this.downflowSelected();
    return (shot.guards || []).filter((g) => {
      if (g.hideWhenDownflow && downflow) return false;
      if (!g.when) return true;
      return Object.entries(g.when).every(([key, value]) =>
        Array.isArray(value) ? value.includes(answers[key]) : answers[key] === value,
      );
    });
  }

  /** 表示中の質問のうち、まだ答えていない最初のキー。全部答えていれば null */
  firstUnansweredGuard(shot) {
    const answers = this.guardAnswers || {};
    const found = this.visibleGuards(shot).find((g) => answers[g.key] == null);
    return found ? found.key : null;
  }

  guardsHtml(shot) {
    const guards = this.visibleGuards(shot);
    if (guards.length === 0) return '';

    const answers = this.guardAnswers || {};
    const attempted = this.guardAttempted === shot.kind;
    const base = (this.dataset.webBase || '').replace(/\/$/, '');
    const src = (path) => (base && path ? this.escape(base + path) : '');

    const figure = (f) => {
      const url = src(f.src);
      if (!url) return '';
      return `<figure class="aicon-iq__guardFig">
                <img src="${url}" alt="${this.escape(f.alt || '')}" loading="lazy" decoding="async">
                ${f.caption ? `<figcaption>${this.escape(f.caption)}</figcaption>` : ''}
              </figure>`;
    };

    // 例の写真は eager にする。lazy だと画面外にあるうちは読み込まれず高さ0のままで、
    // 答えたあとの自動スクロール（scrollSheetToNext）が次の設問の位置を測れない。
    const examples = (ex) => {
      if (!ex || !base) return '';
      return `<div class="aicon-iq__guardExamples">
                <p class="aicon-iq__guardExamplesHead">${this.escape(ex.heading)}</p>
                <div class="aicon-iq__guardExamplesGrid">
                  ${ex.items
                    .map(
                      (item) => `<figure class="aicon-iq__guardExample">
                        <img src="${src(item.src)}" alt="" loading="eager" decoding="async">
                        <figcaption>${this.escape(item.caption)}</figcaption>
                      </figure>`,
                    )
                    .join('')}
                </div>
              </div>`;
    };

    return `
      <div class="aicon-iq__guards" data-iq-guards>
        ${guards
          .map((g) => {
            const value = answers[g.key];
            const unanswered = value == null;
            const note = g.noteWhen && value ? g.noteWhen[value] : '';
            return `
              ${g.examples ? examples(g.examples) : ''}
              ${g.figure ? figure(g.figure) : ''}
              <div class="aicon-iq__guard${attempted && unanswered ? ' is-invalid' : ''}"
                   data-iq-guard="${this.escape(g.key)}"
                   role="group" aria-label="${this.escape(g.label)}">
                <p class="aicon-iq__guardLabel">${this.escape(g.label)}</p>
                <div class="aicon-iq__guardOptions">
                  ${g.options
                    .map(
                      (o) => `<button type="button"
                        class="aicon-iq__guardOption${value === o.value ? ' is-selected' : ''}"
                        data-iq-guard-pick="${this.escape(g.key)}"
                        data-iq-guard-value="${this.escape(o.value)}"
                        aria-pressed="${value === o.value ? 'true' : 'false'}">${this.escape(o.label)}</button>`,
                    )
                    .join('')}
                </div>
                ${note ? `<p class="aicon-iq__guardNote">${this.escape(note)}</p>` : ''}
              </div>
              ${g.figureAfter ? figure(g.figureAfter) : ''}`;
          })
          .join('')}
      </div>
    `;
  }

  guideSheetHtml(shot) {
    const g = shot.guide || {};
    const base = (this.dataset.webBase || '').replace(/\/$/, '');
    const src = (path) => (base && path ? this.escape(base + path) : '');

    const fig = (path, caption, extra = '') => {
      const url = src(path);
      if (!url) return '';
      return `<figure class="aicon-iq__guideFig${extra}">
                <img src="${url}" alt="" loading="lazy" decoding="async">
                ${caption ? `<figcaption>${this.escape(caption)}</figcaption>` : ''}
              </figure>`;
    };

    return `
      <div class="aicon-iq__sheetBack" data-iq-sheet-back hidden></div>
      <div class="aicon-iq__sheet" data-iq-sheet hidden role="dialog" aria-modal="true"
           aria-label="${this.escape(shot.title)}の撮影イメージ">
        <div class="aicon-iq__sheetGrip" aria-hidden="true"></div>
        <div class="aicon-iq__sheetBody">
          <div class="aicon-iq__sheetHead">
            <p class="aicon-iq__sheetTitle">
              <span class="aicon-iq__sheetIcon" aria-hidden="true">🔍</span>撮影イメージ
            </p>
            ${g.lead ? `<p class="aicon-iq__sheetLead">${this.escape(g.lead)}</p>` : ''}
          </div>

          ${
            // ラベルだけ2枚並列（既存の「底面のラベル / 右側面のラベル」）
            Array.isArray(g.pair) && g.pair.length > 0 && base
              ? `<div class="aicon-iq__sheetPair">
                   ${g.pair
                     .map(
                       (item) => `<div class="aicon-iq__sheetPairItem">
                         <span class="aicon-iq__sheetPairLabel">${this.escape(item.label)}</span>
                         <img src="${src(item.src)}" alt="" loading="lazy" decoding="async">
                       </div>`,
                     )
                     .join('')}
                 </div>`
              : ''
          }

          ${g.example ? fig(g.example.src, g.example.caption) : ''}

          ${
            g.point
              ? `<div class="aicon-iq__sheetPoint">
                   <div class="aicon-iq__sheetPointText">
                     <span class="aicon-iq__sheetPointHead">
                       <span class="aicon-iq__sheetPointIcon" aria-hidden="true">💡</span>撮影のポイント
                     </span>
                     <p>${this.escape(g.point.text)}</p>
                   </div>
                   ${g.point.src ? `<img class="aicon-iq__sheetPointImg" src="${src(g.point.src)}" alt="" loading="lazy" decoding="async">` : ''}
                 </div>`
              : ''
          }

          ${
            Array.isArray(g.bad) && g.bad.length > 0 && base
              ? `<p class="aicon-iq__guideBadHead">こうなっていると撮り直しをお願いすることがあります</p>
                 <div class="aicon-iq__guideBadGrid">
                   ${g.bad.map((b) => fig(b.src, b.caption, ' is-bad')).join('')}
                 </div>`
              : ''
          }

          ${g.reference ? fig(g.reference.src, g.reference.caption) : ''}
          ${g.warning ? `<p class="aicon-iq__sheetWarn">${this.escape(g.warning)}</p>` : ''}

          ${this.guardsHtml(shot)}

          <button type="button" class="aicon-iq__cta" data-iq-sheet-pick>写真を撮影・選択する</button>
          <button type="button" class="aicon-iq__link" data-iq-sheet-close>閉じる</button>
        </div>
      </div>
    `;
  }

  /**
   * ガード質問に答える。
   *
   * ★ 選び直したら下の質問を捨てる ★
   *   「配管は見えない」に変えたのに「全部写せる」が残ると、
   *   出ていない質問の回答を backend へ送ることになる。`clears` で消す。
   */
  pickGuard(shot, button) {
    const key = button.dataset.iqGuardPick;
    const value = button.dataset.iqGuardValue;
    if (!key || value === undefined) return;

    this.guardAnswers = { ...(this.guardAnswers || {}), [key]: value };

    // 架台の回答を変えたら、架台の選択肢を作り直す（種類で金額が変わるため選び直してもらう）
    if (key === 'standHas' || key === 'standType') {
      this.standContext = null;
      this.standPlan = null;
    }

    const guard = (shot.guards || []).find((g) => g.key === key);
    for (const dropped of (guard?.clears || {})[value] || []) {
      delete this.guardAnswers[dropped];
    }

    /**
     * ★ 許される枚数が減ったら、溜めた写真を捨てる ★
     * 「写しきれない」で3枚撮ったあとに「1枚で写せる」へ変えると、上限が1枚に戻る。
     * そのまま残すと画面には1枚目しか出ないのに**送信では3枚が飛ぶ**（backend は
     * 質問の答えを見ないので通ってしまう）。見えているものと送るものを一致させる。
     */
    const kept = this.photos.get(shot.kind) || [];
    if (kept.length > this.maxImagesFor(shot.kind)) {
      this.removePhoto(shot.kind);
    }

    this.persistGuardAnswers();

    /**
     * ★ 元の位置に戻してから、下へだけ送る（日高さん指摘 2026-08-28・指示 2026-10-09）★
     * `renderPhotos()` はシートごと作り直すため、新しいシートの `scrollTop` は 0 から
     * 始まる。以前はそのあと答えた設問へ `scrollIntoView({ behavior: 'smooth' })` で
     * 戻していたが、**先頭へ飛んでから滑って戻る**動きになり、押した人からは
     * 「一番上までスクロールされた」ように見えていた（8/28）。
     *
     * そこで作り直す前の位置を控えてそのまま戻し、そこから次の設問（全部答えたら
     * 撮影ボタン）まで**下へだけ**滑らせる（`scrollSheetToNext`・10/09）。上へは動かさない。
     *
     * 未回答のまま「写真を撮影・選択する」を押した経路は、これとは別に
     * その設問まで運ぶ（`[data-iq-sheet-pick]` のハンドラ）。あちらは
     * **動かすことが目的**なので `scrollIntoView` を残している。
     */
    const keptScrollTop = this.photosRoot?.querySelector('[data-iq-sheet]')?.scrollTop ?? 0;
    this.renderPhotos();
    this.openSheet();
    const sheet = this.photosRoot?.querySelector('[data-iq-sheet]');
    if (sheet) {
      sheet.scrollTop = keptScrollTop;
      this.scrollSheetToNext(shot, sheet);
    }
  }

  /**
   * 答えたあと、次に押すところまでシートを送る（日高さん指示 2026-10-09）。
   * 「見えている」→「配管は全部写せますか？」、最後の設問 →「写真を撮影・選択する」。
   *
   * ★ 8/28 の「一番上まで飛ぶ」は繰り返さない ★
   *   あれは作り直したシート（scrollTop = 0）で `scrollIntoView` を呼んだため。
   *   ここでは元の位置へ戻したあと、**そこから下へだけ**滑らせる。上へは戻さない。
   *   すでに見えていれば動かさない。
   *
   * ★ 例の写真が読み終わってから測る ★
   *   「見えている」を選ぶと例の写真4枚が新しく出る。読み込み前は高さ0なので、
   *   すぐ測ると設問の位置を上に見誤る。写真を待つ（上限 1.5 秒）。
   *   待っている間に別の設問を押してシートが作り直されたら、古い方は何もしない。
   */
  scrollSheetToNext(shot, sheet) {
    const pending = this.firstUnansweredGuard(shot);
    const target = pending
      ? sheet.querySelector(`[data-iq-guard="${CSS.escape(pending)}"]`)
      : sheet.querySelector('[data-iq-sheet-pick]');
    if (!target) return;

    const token = (this.sheetScrollToken = (this.sheetScrollToken || 0) + 1);
    const images = [...sheet.querySelectorAll('img')].filter(
      (img) => !img.complete && target.compareDocumentPosition(img) & Node.DOCUMENT_POSITION_PRECEDING,
    );
    const loaded = images.map(
      (img) =>
        new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        }),
    );
    const timeout = new Promise((resolve) => setTimeout(resolve, 1500));

    Promise.race([Promise.all(loaded), timeout]).then(() => {
      if (token !== this.sheetScrollToken || !sheet.isConnected) return;
      const margin = 16;
      const view = sheet.getBoundingClientRect();
      const rect = target.getBoundingClientRect();
      if (rect.bottom <= view.bottom - margin) return; // もう見えている
      // 下端が見えるところまで。ただし設問の上端を画面の外へ押し出さない
      const by = Math.min(rect.bottom - view.bottom + margin, rect.top - view.top - margin);
      if (by <= 0) return;
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      sheet.scrollBy({ top: by, behavior: reduce ? 'auto' : 'smooth' });
    });
  }

  /**
   * 回答を保存する。
   *
   * ★ 写真そのものは持ち回さない ★
   *   回答はテキストだけなので localStorage に置ける。ページを離れて戻っても
   *   同じ質問をやり直さずに済む。写真（base64）は容量を食うので入れない。
   */
  persistGuardAnswers() {
    try {
      sessionStorage.setItem(guardStorageKey(this.modelNo), JSON.stringify(this.guardAnswers || {}));
    } catch (_e) {
      // 保存できなくても購入は止めない（プライベートブラウズ等）
    }
  }

  /**
   * 保存した回答を読み戻す。読めなければ空。
   *
   * ★ いまの選択肢に無い値は捨てる（2026-09-18）★
   *   選択肢を減らしたとき（ベランダの「わからない」を外した）、保存済みの値が
   *   そのまま戻ると **どのボタンも選ばれていないのに回答済みになる**。
   *   `firstUnansweredGuard` は `answers[key] == null` で見るため、
   *   `'unknown'` は「答えた」と数えられて先へ進めてしまい、その値が送られる。
   *
   *   キー（`v2`）は上げていない。上げると**有効な回答まで全部消えて聞き直しになる**ため、
   *   無効になった値だけを落とす。∴ 選択肢を減らすだけならキーの更新は要らない
   *   （**設問文や値の意味が変わったときは今後もキーを上げる**）。
   */
  restoreGuardAnswers() {
    try {
      const raw = sessionStorage.getItem(guardStorageKey(this.modelNo));
      const parsed = raw ? JSON.parse(raw) : null;
      if (!parsed || typeof parsed !== 'object') return {};
      return this.dropUnknownGuardValues(parsed);
    } catch (_e) {
      return {};
    }
  }

  /**
   * いまの設問定義に無い値を落とす。
   * 定義そのものから許容値を組むので、将来どの設問の選択肢を減らしても効く。
   */
  dropUnknownGuardValues(answers) {
    const allowed = new Map();
    for (const shot of SHOTS) {
      for (const g of shot.guards || []) {
        if (!g.key || !Array.isArray(g.options)) continue;
        const set = allowed.get(g.key) || new Set();
        for (const o of g.options) set.add(o.value);
        allowed.set(g.key, set);
      }
    }
    const out = {};
    for (const [key, value] of Object.entries(answers)) {
      const set = allowed.get(key);
      // 定義に無いキーは判断材料が無いので触らない（別用途の保存値を消さない）
      if (!set || set.has(value)) out[key] = value;
    }
    return out;
  }

  /*
   * ★ 階数の初期値入れ（seedFloorFromMethod）は廃止した（2026-09-15）★
   *   2026-08-31 から「2階→1階を申告した人の floor に '2階' を入れておく」処理があったが、
   *   **初期値を入れるだけで質問は出たまま**だったため、お客様が「1階」に直せてしまい
   *   `applyGuardAnswers` が高所作業費を落としていた（#1049。当時のコメントにも
   *   「申告と回答が食い違ったまま金額が動く」と書かれていた）。
   *
   *   いまは質問そのものを出さず（`hideWhenDownflow`）、申告を `/photos/finish` の
   *   `downflow` で送って backend が高所作業費を確定させる。∴ 初期値は要らない。
   *
   *   戻さないこと: 初期値方式は「購入ボックスの申告を上書きできる」状態を必ず作る。
   */

  /** 写真ステップのイベントを繋ぐ。renderPhotos が innerHTML を作り直すたびに呼ぶ */
  bindPhotoHandlers(shot) {
    const root = this.photosRoot;
    const file = root.querySelector(`[data-iq-file="${shot.kind}"]`);

    file?.addEventListener('change', (event) => this.onPickPhoto(shot.kind, event));

    // 「撮影する」→ 撮影イメージのシートを開く（いきなりカメラを開かない）
    root.querySelector('[data-iq-open-guide]')?.addEventListener('click', () => this.openSheet());
    root.querySelector('[data-iq-sheet-close]')?.addEventListener('click', () => this.closeSheet());
    root.querySelector('[data-iq-sheet-back]')?.addEventListener('click', () => this.closeSheet());
    // ガード質問の選択。押すたびに保存してシートを描き直す
    for (const button of root.querySelectorAll('[data-iq-guard-pick]')) {
      button.addEventListener('click', () => this.pickGuard(shot, button));
    }

    // シートの「写真を撮影・選択する」でカメラ／ライブラリを開く
    //
    // ★ 未回答があるうちはカメラを開かない（web版 PhotoView と同じ）★
    //   押せなくするのではなく、押したら未回答の質問まで運んで赤く示す。
    //   disabled にすると「なぜ押せないのか」が伝わらず、そこで止まる。
    root.querySelector('[data-iq-sheet-pick]')?.addEventListener('click', () => {
      const pending = this.firstUnansweredGuard(shot);
      if (pending) {
        this.guardAttempted = shot.kind;
        this.renderPhotos();
        this.openSheet();
        requestAnimationFrame(() => {
          this.photosRoot
            ?.querySelector(`[data-iq-guard="${pending}"]`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return;
      }
      this.closeSheet();
      file?.click();
    });

    // 撮り直しは、いま出ている写真を消してシートから撮り直す
    root.querySelector('[data-iq-retake]')?.addEventListener('click', () => {
      this.removePhoto(shot.kind);
      this.openSheet();
    });

    root
      .querySelector('[data-iq-next-shot]')
      ?.addEventListener('click', () => this.goToShot(this.shotIndex + 1));

    for (const dot of root.querySelectorAll('[data-iq-dot]')) {
      dot.addEventListener('click', () => this.goToShot(Number(dot.dataset.iqDot)));
    }

    // 「もう1枚追加する」はガード質問を通さずカメラへ（1枚目で既に答えているため）
    root.querySelector('[data-iq-add-image]')?.addEventListener('click', () => file?.click());

    // 追加した写真を1枚だけ取り消す
    for (const drop of root.querySelectorAll('[data-iq-drop-image]')) {
      drop.addEventListener('click', () => this.dropImage(shot.kind, Number(drop.dataset.iqDropImage)));
    }

    // 貯めた写真をまとめて解析する
    root.querySelector('[data-iq-analyze-shot]')?.addEventListener('click', () => this.analyzeShot(shot.kind));

    // 「この写真で見積を確定する」→ 先にカバーの仕上げを選んでもらう
    root.querySelector('[data-iq-analyze]')?.addEventListener('click', () => this.openCoverSheet());

    for (const button of root.querySelectorAll('[data-iq-cover-plan]')) {
      button.addEventListener('click', () => this.pickCoverPlan(button.dataset.iqCoverPlan));
    }
    for (const button of root.querySelectorAll('[data-iq-interior-plan]')) {
      button.addEventListener('click', () => this.pickInteriorPlan(button.dataset.iqInteriorPlan));
    }
    for (const button of root.querySelectorAll('[data-iq-stand-plan]')) {
      button.addEventListener('click', () => this.pickStandPlan(button.dataset.iqStandPlan));
    }
    for (const button of root.querySelectorAll('[data-iq-cover-color]')) {
      button.addEventListener('click', () => this.pickCoverColor(button.dataset.iqCoverColor));
    }
    root.querySelector('[data-iq-cover-done]')?.addEventListener('click', () => this.analyze());
    // 背景を押しても閉じない（選ばずに進む導線を作らない）。閉じるのは確定だけ。
    root
      .querySelector('[data-iq-skip-analyze]')
      ?.addEventListener('click', (event) => this.skipPhotos(event.currentTarget));
  }

  /**
   * カバーの判定と選択を両方捨てる。
   *
   * ★ 判定だけ捨てて選択を残さない（レビュー指摘 #1327 / #1329）★
   * どの写真を撮り直してもカバーの前提は変わる。室外機を撮り直せば状態が変わり、
   * 型番ラベルを撮り直せば発売年が変わる。判定（context）だけ捨てて選択（plan）を
   * 残すと、次のような**選んでいないのに確定できる**状態が起きる。
   *
   *   ① 「既存カバーを再利用する」を選ぶ（coverPlan = 'reuse'）
   *   ② 室外機を撮り直す → 今度は「カバーなし」と判定される
   *   ③ シートを開き直すと「再利用する」ボタンは**描画されない**（canReuse が false）
   *   ④ それでも coverPlan は 'reuse' のままなので確定ボタンが押せる
   *
   * 金額はサーバが `parseCoverSelection` で補正するので誤課金にはならないが、
   * お客様からは**何も選んでいないのに新品カバー代（5,500円）で確定した**ように見える。
   * 写真が変われば選び直してもらう。
   */
  forgetCoverChoice() {
    this.coverContext = null;
    this.indoorContext = null;
    this.coverPlan = null;
    this.coverColor = null;
    this.interiorPlan = null;
    // 架台も同じ。室外機を撮り直せば架台の判定が、ラベルを撮り直せば発売年が変わる
    this.standContext = null;
    this.standPlan = null;
  }

  /**
   * 追加撮影のうち1枚だけ取り消す。
   *
   * 最後の1枚を消したら「まだ何も撮っていない」状態に戻す（空配列を残すと
   * `photos.has(kind)` が true のままドットが撮影済みに見える）。
   */
  dropImage(kind, index) {
    // 解析中は消させない（multiHtml で × を出していないが、古い描画が残っていても受けない）
    if (this.shotState.get(kind) === 'analyzing') return;
    const images = this.photos.get(kind) || [];
    const next = images.filter((_, i) => i !== index);
    if (next.length === 0) {
      this.removePhoto(kind);
      return;
    }
    this.photos.set(kind, next);
    // 枚数が変わったら前の解析結果は根拠にならない
    this.shotState.set(kind, 'pending');
    this.shotRecog.delete(kind);
    this.analysis = null;
    this.renderPhotos();
  }

  openSheet() {
    const back = this.photosRoot?.querySelector('[data-iq-sheet-back]');
    const sheet = this.photosRoot?.querySelector('[data-iq-sheet]');
    if (back) back.hidden = false;
    if (sheet) {
      sheet.hidden = false;
      /**
       * ★ focus でスクロールさせない（日高さん指摘 2026-08-28）★
       * `.focus()` は既定でその要素まで**画面を動かす**。シートは
       * `position: fixed; max-height: 88vh; overflow-y: auto`（CSS :1348）で
       * それ自身がスクロール容器なので、focus するたび**先頭（撮影イメージ）まで
       * 巻き戻っていた**。設問に答えるたびに毎回上へ飛ぶ原因がこれ。
       * 読み上げの起点としての focus は要るので、移動だけを止める。
       */
      sheet.focus?.({ preventScroll: true });
    }
  }

  closeSheet() {
    const back = this.photosRoot?.querySelector('[data-iq-sheet-back]');
    const sheet = this.photosRoot?.querySelector('[data-iq-sheet]');
    if (back) back.hidden = true;
    if (sheet) sheet.hidden = true;
  }

  /**
   * 1枚選ばれたときの処理。
   *
   * ★ HEAD には実体が無かった（2026-08-20 に発覚）★
   * `renderPhotos` が `this.onPickPhoto` を change に繋いでいたが、メソッドが
   * どこにも定義されておらず、`this.photos.set(...)` も1か所も無かった。
   * つまり**写真を選んでも保存されず、1枚も撮れない**状態だった。
   * 「あと4枚でAI判定できます」が永久に減らないのはこれが原因。
   *
   * ★ 撮ったその場で解析する ★
   * 既存の AiCON（診断SPA の PhotoView）と同じ。4枚ためて最後に1回ではなく、
   * 1枚ごとに `/photos/shot` を叩いて「メーカーを認識しました Daikin」を見せる。
   *
   * ★ 世代を採番して古い応答を捨てる ★
   * 解析中に撮り直されると、前の写真の認識結果が後から届いて上書きしうる。
   * kind ごとに世代を持ち、最新の要求以外は無視する。
   */
  async onPickPhoto(kind, event) {
    const file = event?.target?.files?.[0];
    // 選択をキャンセルされた。同じ写真をもう一度選べるよう value は必ず空にする
    if (event?.target) event.target.value = '';
    if (!file) return;
    // ★ 解析中は受けない（multiHtml の注記・#1156・設計書 165-8）★
    //   ボタンを隠しても、開いていたファイル選択から戻ってくることはある。ここで足すと解析中の写真ごと
    //   送り直され、あとから終わった古い解析の結果で見積が上書きされる。
    if (this.shotState.get(kind) === 'analyzing') return;

    this.clearError();

    let shrunk;
    try {
      shrunk = await this.shrinkImage(file);
    } catch {
      this.showError('この写真を読み込めませんでした。別の写真でお試しください。');
      return;
    }

    this.shotRecog.delete(kind);
    this.shotReason.delete(kind);

    // ★ 撮り直しで前回の確定結果を持ち越さない ★
    //   写真が変わったのに古い判定が残っていると、STEP3 が前の写真の金額を出す。
    this.analysis = null;

    /**
     * ★ 複数枚は貯めてから1回だけ解析する ★
     * web版（PhotoView.tsx の追加撮影バッファ）と同じ。撮るたびに解析し直すと、
     * 5枚なら AI を5回呼ぶことになり **AI クォータ（REQ-405）を枚数ぶん減らす**。
     * 判定も「全部の写真を総合して」出すものなので、途中の1枚だけで出す意味がない。
     */
    if (this.maxImagesFor(kind) > 1) {
      const current = this.photos.get(kind) || [];
      if (current.length >= this.maxImagesFor(kind)) return;
      this.photos.set(kind, [...current, shrunk]);
      this.shotState.set(kind, 'pending');
      this.renderPhotos();
      return;
    }

    this.photos.set(kind, [shrunk]);
    this.shotState.set(kind, 'analyzing');
    await this.analyzeShot(kind);
  }

  /**
   * 溜まっている写真をまとめて解析する。
   *
   * ★ 世代を採番して古い応答を捨てる ★
   * 解析中に撮り直されると、前の写真の認識結果が後から届いて上書きしうる。
   * kind ごとに世代を持ち、最新の要求以外は無視する。
   */
  async analyzeShot(kind) {
    const images = this.photos.get(kind) || [];
    if (images.length === 0) return;

    this.forgetCoverChoice();

    this.clearError();
    this.shotState.set(kind, 'analyzing');

    const epoch = (this.shotEpoch.get(kind) || 0) + 1;
    this.shotEpoch.set(kind, epoch);

    this.renderPhotos();

    try {
      const result = await this.call('/photos/shot', {
        method: 'POST',
        // 1枚でも配列で送る（backend は両方の形を受けるが、送る側で形を分けない）。
        body: { kind, imagesBase64: images.map((one) => one.base64) },
      });
      if (this.shotEpoch.get(kind) !== epoch) return; // 撮り直された。古い応答は捨てる

      if (result.ok) {
        this.shotState.set(kind, 'done');
        this.shotRecog.set(kind, Array.isArray(result.recognized) ? result.recognized : []);
      } else {
        // 解析できなかった。写真は残さず撮り直してもらう
        this.photos.delete(kind);
        this.shotState.set(kind, 'retake');
        this.shotReason.set(kind, result.reason || null);
      }
    } catch (error) {
      if (this.shotEpoch.get(kind) !== epoch) return;
      this.photos.delete(kind);
      this.shotState.set(kind, 'retake');
      this.shotReason.set(
        kind,
        error.message || 'ただいま写真を確認できません。時間をおいてお試しください。',
      );
    }

    this.renderPhotos();
  }

  /** 指定の写真へ移る。範囲外は端で止める */
  goToShot(index) {
    this.shotIndex = Math.min(SHOTS.length - 1, Math.max(0, index));
    this.renderPhotos();
  }

  removePhoto(kind) {
    this.photos.delete(kind);
    this.forgetCoverChoice();
    // ★ 付随する state も一緒に落とす ★
    //   残すと、消したはずの写真の認識結果が次の描画で出る。
    //   世代は進める（進行中の解析の応答を無効にする）。
    this.shotState.delete(kind);
    this.shotRecog.delete(kind);
    this.shotReason.delete(kind);
    this.shotEpoch.set(kind, (this.shotEpoch.get(kind) || 0) + 1);
    this.analysis = null;
    this.renderPhotos();
  }

  /**
   * 送る前に縮める。
   *
   * ★ そのまま送らない ★
   * スマホの写真は1枚 3〜8MB あり、4枚で上限（20MB）を超えうる。加えて解析は実測で
   * 19秒かかっており、大きい画像ほど遅くなる。長辺 1600px / JPEG 0.8 まで落としても
   * 型番ラベルやコンセントの表示は読める。
   *
   * data URI の接頭辞は落として**純粋な base64** を渡す（backend の契約）。
   */
  shrinkImage(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);

        const dataUrl = canvas.toDataURL('image/jpeg', IMAGE_QUALITY);
        resolve({ dataUrl, base64: dataUrl.split(',')[1] });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('image_load_failed'));
      };
      img.src = url;
    });
  }

  /** 解析中の表示。何を確認しているかを項目別に出す（REQ-203） */
  analyzingHtml() {
    return `
      <div class="aicon-iq__analyzing">
        <span class="aicon-iq__bigSpinner" aria-hidden="true"></span>
        <h3 class="aicon-iq__analyzingTitle">写真を確認しています</h3>
        <p class="aicon-iq__lead">
          20秒ほどお待ちください。配管の経路と室外機の置き場所を見ています。
        </p>
        <ul class="aicon-iq__checks" data-iq-checks>
          ${SHOTS.map(
            (shot, i) => `
            <li class="aicon-iq__check" data-iq-check="${i}">
              <span class="aicon-iq__checkMark" aria-hidden="true">✓</span>
              ${this.escape(shot.title)}を確認しています
            </li>`,
          ).join('')}
        </ul>
        <p class="aicon-iq__zipHint">
          お写真は工事の判定と当日の準備にのみ使用し、判定後に削除します。
        </p>
        <button type="button" class="aicon-iq__ghost" data-iq-cancel-analyze>キャンセルする</button>
      </div>
    `;
  }

  /**
   * 4枚を送って解析する。
   *
   * ★ 待たせている間に何をしているか見せる ★
   * 実測で19秒かかる。無言で待たせると離脱するため、項目を順に点灯させる。
   * 点灯はあくまで演出で、実際の解析は4枚並列（backend 側）。
   */
  async analyze() {
    // ★ 撮影枚数ではなく判定の完了で見る ★
    //   複数枚モードで貯めている途中（pending）に確定させない。
    if (this.analyzedCount() !== SHOTS.length) return;

    // ★ 遅れて届いた応答で画面を書き換えない ★
    // 実測19〜36秒かかるため、待ちきれずキャンセル→撮り直し→再判定という
    // 操作が現実に起きる。世代（epoch）を採番し、**最新の要求以外は捨てる**。
    // これが無いと、古い写真の判定結果で STEP3 に飛ばされたり、backend 側の
    // `analysisSnapshot` が後勝ちで古い findings に戻ったりする（＝確定額が狂う）。
    const epoch = (this.analyzeEpoch = (this.analyzeEpoch || 0) + 1);
    this.abortAnalyze();
    const controller = new AbortController();
    this.analyzeAbort = controller;

    this.analyzing = true;
    this.analysis = null;
    this.clearError();
    // ★ 確定中は写真の画面を作り直さない ★
    //   1枚ずつの解析が済んでいるので、ここは findings の確定だけ。
    //   確認項目を順に点灯させる表示（analyzingHtml + ticker）に差し替える。
    this.photosRoot.innerHTML = this.analyzingHtml();
    this.photosRoot
      .querySelector('[data-iq-cancel-analyze]')
      ?.addEventListener('click', () => this.cancelAnalyze());
    this.startCheckTicker();
    // 解析中は戻れない。戻ってから応答が届くと STEP3 へ強制的に飛ばされる
    this.syncBackButton();

    try {
      // ★ 画像は送らない ★
      //   1枚ずつ `/photos/shot` で解析済みで、生データは backend の
      //   `analysisSnapshot` に貯まっている。ここは findings の確定だけ。
      //   20MB を2回運ばせない。
      const result = await this.call('/photos/finish', {
        method: 'POST',
        // ★ ボトムシートの回答を一緒に送る（2026-08-26）★
        //   階数と架台は backend が findings に重ねて金額へ反映する
        //   （backend/src/services/analysis/guardAnswers.ts）。
        //   読めない値は捨てられるだけなので、未回答が混ざっても 400 にはならない。
        body: {
          answers: this.guardAnswers || {},
          /**
           * ★ 「2階→1階」の申告を送る（2026-09-15）★
           *   申告した人には階数・ベランダを聞かない（`hideWhenDownflow`）ので、
           *   `answers` では表せない。backend はこれを見て高所作業費を確定させる
           *   （`applyGuardAnswers` の options.downflow）。写真判定を通さない経路が
           *   `downflow_preset` で無条件に計上しているのと同じ結論にする。
           */
          downflow: this.downflowSelected(),
          /**
           * 配管カバーの仕上げ。backend が **判定を引き直して照合**したうえで採用する
           * （`parseCoverSelection`）。ここで送った値がそのまま金額になるわけではない。
           */
          ...(this.coverPlan
            ? { coverSelection: { exteriorPlan: this.coverPlan, color: this.coverColor } }
            : {}),
          // 室内カバー。サーバが「カバーがある」と判定した部屋でだけ採用される
          ...(this.interiorPlan ? { interiorCoverPlan: this.interiorPlan } : {}),
          // 架台（再利用 / 交換）。サーバが判定し直し、再利用できない架台は交換にする
          ...(this.standPlan ? { standPlan: this.standPlan } : {}),
        },
        signal: controller.signal,
      });
      if (epoch !== this.analyzeEpoch) return; // 古い要求の応答。捨てる

      this.analysis = result;
      /*
       * ★ この判定がどちらの申告で出たかを控える（2026-09-15）★
       *   高所作業費は申告（`downflow`）で決まるのに、`/quote/confirm` は
       *   保存済みの findings をそのまま使う（再導出しない設計）。∴ 判定のあとに
       *   工事条件を切り替えられると、**古い申告の金額のまま確定してしまう**。
       *   `enterResult()` がここと突き合わせて、変わっていれば判定を取り直す。
       */
      this.analyzedDownflow = this.downflowSelected();
      this.finishAnalyze();
      // 判定できてもできなくても結果ステップへ進む。inconclusive は正常な結果
      this.goTo(3);
      this.dispatchEvent(new CustomEvent('aicon-iq:analyzed', { detail: result }));
    } catch (error) {
      if (epoch !== this.analyzeEpoch) return; // キャンセル済み。何も出さない

      this.finishAnalyze();
      this.renderPhotos();
      this.handleAnalyzeError(error);
    }
  }

  /** 解析中の状態を畳む。ticker・中断ハンドル・戻るボタンをまとめて戻す */
  finishAnalyze() {
    this.stopCheckTicker();
    this.analyzing = false;
    this.analyzeAbort = null;
    this.syncBackButton();
  }

  /** 進行中の /photos を実際に中断する（無ければ何もしない） */
  abortAnalyze() {
    if (!this.analyzeAbort) return;
    this.analyzeAbort.abort();
    this.analyzeAbort = null;
  }

  /**
   * 解析の失敗を nextAction で振り分ける。
   *
   * backend は 4xx で返すため、`code` / `message` / `nextAction` がここまで届く
   * （5xx だと App Proxy がボディを HTML に置き換えてしまう）。
   */
  handleAnalyzeError(error) {
    if (error.nextAction === 'contact_staff') {
      // 双方のプロバイダが失敗。担当者確認へ回す（EDGE-002）
      this.analysis = { status: 'inconclusive', findings: [], reason: error.message };
      this.goTo(3);
      return;
    }
    // IMAGE_TOO_LARGE など。撮り直してもらう（EDGE-103）
    this.showError(error.message || '写真を確認できませんでした。撮り直してお試しください。');
  }

  /**
   * 写真なしで進む（REQ-301）。担当者が後から確認する前提。
   *
   * ★ 判定結果の画面を挟まない（2026-08-20）★
   *   STEP1 の「写真なしで進む」と同じ扱い。1枚も出していない人に
   *   「写真だけでは判断できませんでした」を見せる意味が無い。
   */
  skipPhotos(trigger) {
    this.analysis = { status: 'inconclusive', findings: [], reason: null, skipped: true };
    this.contactStaff(trigger);
  }

  // ------------------------------------------------------------------
  // 延長保証（2026-08-21 に購入ボックスから移設）
  // ------------------------------------------------------------------

  /**
   * 購入ボックスが持っている延長保証の情報を読む。
   *
   * ★ 金額とバリアントIDをここに焼き込まない ★
   *   唯一の出どころは Shopify のバリアント。sections/aicon-product-buybox.liquid が
   *   [data-aicon-warranty] の data 属性に出しているので、それを読む。
   *   保証の付いていない機種では要素が無く、その場合は訴求そのものを出さない。
   */
  warrantyInfo() {
    const host = document.querySelector('[data-aicon-warranty]');
    const select = document.querySelector('[data-aicon-warranty-select]');
    if (!host || !select) return null;
    const variantId = (host.dataset.warrantyVariantId || '').trim();
    if (!variantId) return null;
    return {
      select,
      variantId,
      price: host.dataset.warrantyPrice || '',
      heading: this.dataset.warrantyHeading || host.dataset.warrantyHeading || '延長保証',
      lead: this.dataset.warrantyLead || '',
      detail: this.dataset.warrantyDetail || host.dataset.warrantyNote || '',
      joinLabel: this.dataset.warrantyJoin || 'つけてカートに入れる',
      skipLabel: this.dataset.warrantySkip || 'つけずにカートに入れる',
      optionalLabel: host.dataset.warrantyOptionalLabel || '任意',
      termsLabel: this.dataset.warrantyTermsLabel || '延長保証とは？',
    };
  }

  /**
   * 延長保証を購入欄（PC の③延長保証）で選び終えているか（2026-11-01・ロキさんの PC 版デザイン）。
   *
   * ★ 「欄が画面に出ているか」で判定する ★
   *   ③延長保証は PC（画面幅 990px 以上）で工事つきを選んだときだけ表示される
   *   （sections/aicon-product-buybox.liquid の [data-aicon-pc-warranty]）。出ているなら、
   *   お客様は「加入しない／加入する」のどちらかを選んだ状態（既定は加入しない）。
   *   スマホ・989px 以下では CSS で出していないので false になり、今までどおりシートで聞く。
   *   幅で判定せず表示で判定するのは、CSS の切り替え幅と二重に持たないため。
   */
  warrantyChosenInBuybox() {
    const pcWarranty = document.querySelector('[data-aicon-pc-warranty]');
    return !!pcWarranty && !pcWarranty.hidden && pcWarranty.getClientRects().length > 0;
  }

  /**
   * カートへ入れる直前に延長保証をたずねる。
   *
   * @param {Function} resume 答えが決まったあとに呼び直す処理
   * @param {HTMLElement} [trigger] 押されたボタン（閉じたときに戻す）
   * @returns {boolean} シートを出したら true（呼び出し側はそこで処理を止める）
   *
   * ★ 一度きり ★
   *   this.warrantyAsked を立てて、2回目以降は素通りさせる。立てないと
   *   resume がこの関数に戻ってくるので無限に開き続ける。
   */
  openWarrantySheet(resume, trigger) {
    if (this.warrantyAsked) return false;
    // PC は購入欄の③延長保証で選び終えている。聞き直すと答えが select を上書きする
    if (this.warrantyChosenInBuybox()) return false;
    const sheet = this.querySelector('[data-iq-warranty-sheet]');
    const body = this.querySelector('[data-iq-warranty-body]');
    const info = this.warrantyInfo();
    // 保証の無い機種・要素が無いテーマでは訴求を挟まずそのまま進む
    if (!sheet || !body || !info) return false;

    body.innerHTML = this.warrantyHtml(info, 'sheet');
    sheet.hidden = false;
    // ★ シートを出している間は元のボタンを押させない ★
    //   スクリムが覆うので普通は押せないが、押せた場合はシートが描き直されて
    //   カート投入が2回走りうる。開いた時点で止め、閉じたときだけ戻す。
    this.holdButton(trigger);

    const finish = (join) => {
      this.warrantyAsked = true;
      // 値の置き場所は購入ボックスの select 1箇所だけ（カート投入の3経路が読む）
      info.select.value = join ? info.variantId : '';
      sheet.hidden = true;
      // ★ 押したボタンを必ず戻す ★
      //   シートを出すときに二重押し防止で disabled にしている。戻さずに resume()
      //   すると、呼び直した contactStaff が冒頭の `if (trigger.disabled) return;`
      //   で即座に抜け、**カートに何も入らないまま画面が止まる**。
      //   （2026-08-21 のレビューで検出。二重押し防止を足した副作用）
      this.releaseButton(trigger);
      resume();
    };

    body.querySelector('[data-iq-warranty-join]')?.addEventListener('click', () => finish(true));
    body.querySelector('[data-iq-warranty-decline]')?.addEventListener('click', () => finish(false));
    body.querySelector('[data-iq-terms-open]')?.addEventListener('click', () => this.openTerms());
    // 背景を押して閉じたときは「決めなかった」扱い。押したボタンを戻して同じ画面に留める
    sheet.querySelector('[data-iq-warranty-dismiss]')?.addEventListener(
      'click',
      () => {
        sheet.hidden = true;
        this.releaseButton(trigger);
      },
      { once: true },
    );
    return true;
  }

  /**
   * 保証の訴求カード。ボトムシートと判定結果の両方で同じ形を使う。
   * @param {'sheet'|'result'} place 出す場所。ボタンの文字が変わる
   */
  warrantyHtml(info, place) {
    const price = info.price ? `<span class="aicon-iq__warrantyPrice">${this.escape(info.price)}</span>` : '';
    const lead = info.lead ? `<p class="aicon-iq__warrantyLead">${this.escape(info.lead)}</p>` : '';
    const detail = info.detail ? `<p class="aicon-iq__warrantyDetail">${this.escape(info.detail)}</p>` : '';
    const join = place === 'result' ? '延長保証をつける' : info.joinLabel;
    const skip = place === 'result' ? 'つけない' : info.skipLabel;
    // ★「延長保証って何？」に答える導線（日高さん指示 2026-08-21）★
    //   規約ページが無いテーマでは liquid 側が器を出さないので、リンクも出さない。
    const terms = this.querySelector('[data-iq-terms]')
      ? `<button type="button" class="aicon-iq__termsLink" data-iq-terms-open>${this.escape(
          info.termsLabel,
        )}</button>`
      : '';
    return `
      <div class="aicon-iq__warranty">
        <p class="aicon-iq__warrantyHead">
          <span class="aicon-iq__warrantyTag">${this.escape(info.optionalLabel)}</span>
          <span class="aicon-iq__warrantyTitle" id="aiconWarrantySheetTitle">${this.escape(info.heading)}</span>
        </p>
        ${price}
        ${lead}
        ${terms}
        ${detail}
        <button type="button" class="aicon-iq__cta" data-iq-warranty-join>${this.escape(join)}</button>
        <button type="button" class="aicon-iq__ghost" data-iq-warranty-decline>${this.escape(skip)}</button>
      </div>`;
  }

  /**
   * 延長保証規約の簡易モーダルを開く。
   *
   * ★ ページへ遷移させない（日高さん指示 2026-08-21）★
   *   /pages/warranty-extended へ飛ばすと購入フローから離れて戻ってこない。
   *   中身は liquid が同じページの本文をそのまま埋めてあるので、出すだけでよい。
   *   要素が無い（ページが見つからない）ときは起動リンクも出していないため、
   *   ここへは来ない。来ても何もしない。
   */
  openTerms() {
    const terms = this.querySelector('[data-iq-terms]');
    if (!terms) return;
    terms.hidden = false;
    terms.querySelector('.aicon-iq__termsPanel')?.focus?.();
  }

  closeTerms() {
    const terms = this.querySelector('[data-iq-terms]');
    if (terms) terms.hidden = true;
  }

  /**
   * 判定結果の画面に保証の訴求を出す（写真を4枚出した人の導線）。
   *
   * ★ ここはボトムシートにしない ★
   *   結果の画面はそのままカートへ進む画面なので、下からせり上げると
   *   金額の確認を隠してしまう。結果の中に並べる。
   */
  renderWarrantyInResult() {
    const info = this.warrantyInfo();
    if (!info || !this.resultRoot || this.warrantyAsked) return;
    if (this.warrantyChosenInBuybox()) return;
    const box = document.createElement('div');
    box.innerHTML = this.warrantyHtml(info, 'result');
    this.resultRoot.appendChild(box);
    const mark = (join) => {
      this.warrantyAsked = true;
      info.select.value = join ? info.variantId : '';
      /**
       * ★ 選んだ後は問いごと消す（日高さん指摘 2026-08-28）★
       *
       * 以前は見出しを流用して「延長保証をお付けしますか？をお付けします」と出しており、
       * 文として通っていなかった。文言を直すより**表示自体を消す**のが正しい。
       * ここはお客様が延長保証を選び終えた後で、結果は**この下の金額**（延長保証つきの
       * 総額）に出る。同じことを文でも言うと、確定の画面に読むものが増えるだけになる。
       *
       * 押しても何も起きないようには見えない。問いが消えること自体が反応で、
       * つけた場合は総額が変わる。
       */
      box.remove();
    };
    box.querySelector('[data-iq-warranty-join]')?.addEventListener('click', () => mark(true));
    box.querySelector('[data-iq-warranty-decline]')?.addEventListener('click', () => mark(false));
    box.querySelector('[data-iq-terms-open]')?.addEventListener('click', () => this.openTerms());
  }

  cancelAnalyze() {
    // ★ 送信も止める ★
    // 表示だけ戻すと fetch は生き続け、遅れて届いた応答が画面と
    // `analysisSnapshot` を上書きする。epoch を進めて応答を無効にし、
    // AbortController で通信自体も切る。
    this.analyzeEpoch = (this.analyzeEpoch || 0) + 1;
    this.abortAnalyze();
    this.stopCheckTicker();
    this.analyzing = false;
    this.syncBackButton();
    this.renderPhotos();
  }

  /** 確認項目を順に点灯させる。解析の実所要（実測19秒）に合わせて間隔を取る */
  startCheckTicker() {
    let index = 0;
    this.checkTimer = setInterval(() => {
      const item = this.photosRoot?.querySelector(`[data-iq-check="${index}"]`);
      if (item) item.classList.add('is-done');
      index += 1;
      if (index >= SHOTS.length) this.stopCheckTicker();
    }, 4000);
  }

  stopCheckTicker() {
    if (this.checkTimer) clearInterval(this.checkTimer);
    this.checkTimer = null;
  }

  // ------------------------------------------------------------------
  // STEP 3: 判定結果と金額確定
  // ------------------------------------------------------------------

  /**
   * Step3 に来たら確定を呼ぶ。
   *
   * ★ 判定できなかった場合は確定を呼ばない ★
   * 金額の根拠が無いため backend も 409 で弾く。担当者確認へ回す画面を出す。
   */
  async enterResult() {
    this.clearError();

    if (this.analysis?.status !== 'analyzed') {
      this.renderInconclusive();
      return;
    }

    /*
     * ★ 判定のあとに工事条件を切り替えられていたら取り直す（2026-09-15）★
     *   購入ボックスの「標準工事 / 2階→1階」はモーダルの外にあり、閉じている間に
     *   変えられる。高所作業費はこの申告で決まる（`applyGuardAnswers` の
     *   options.downflow）のに、`/quote/confirm` は保存済みの findings を正とするため、
     *   取り直さないと **申告を取り下げた人に高所作業費を請求し続ける**（過大請求）。
     *
     *   画像は送り直さない（`analyze()` の ★ 参照）。生データから findings を
     *   組み直すだけなので、お客様に撮り直しは発生しない。
     *   `analyze()` は成功すると自分で `goTo(3)` を呼ぶ。そのときは申告が一致するので
     *   ここは素通りになり、往復は1回で止まる。
     */
    if (this.analyzedDownflow !== this.downflowSelected()) {
      this.goTo(2);
      await this.analyze();
      return;
    }

    this.resultRoot.innerHTML = '<p class="aicon-iq__areaLoading">金額を確定しています…</p>';
    try {
      const quote = await this.call('/quote/confirm', { method: 'POST', body: {} });
      this.quote = quote;
      this.renderQuote(quote);
      // 写真を出した人はここで保証をたずねる（日高さん指示 2026-08-21）
      this.renderWarrantyInResult();
    } catch (error) {
      this.resultRoot.innerHTML = '';
      this.showError(error.message || 'ただいま金額を確定できません。時間をおいてお試しください。');
    }
  }

  /**
   * 確定額を描く。
   *
   * ★ 金額はすべてレスポンス由来 ★
   * 合計も差額もここで計算しない。サーバーが返した値だけを出す（REQ-006）。
   * `additionalTotal` は backend が明細の単純合計として返している。
   */
  /**
   * 選んだ配管カバーを [見出し, 値] の組で返す（エスケープ前の素の文字列）。
   *
   * 確定画面の行（coverLineHtml）とカートの行の property（buildCartItems）の
   * 両方がここを読む。文言を2箇所に持つと、画面とお店側の注文画面で名前が変わる。
   *
   * ★ サーバの確定値を出す ★
   * こちらが持っている `coverPlan` ではなく、`/quote/confirm` が返した
   * `installPlan` を使う。再利用できない状態で reuse を送っていた場合、
   * サーバが新品へ倒しているため食い違う。
   */
  coverRows(quote) {
    // ★ 選択ボタンと同じ文言にする ★
    //   ここだけ旧文言が残ると、選んだときと確定したときで名前が変わって見える。
    const OUTDOOR = {
      cover: '配管カバー新品',
      cover_reuse: '既存カバーを再利用する',
      standard: 'テープ巻き（標準仕上げ）',
    };
    const INDOOR = { new: '室内カバー新品', reuse: '室内カバー再利用' };

    // 色もサーバの保存値を先に見る。手元の値は確定後にページを読み直すと消える
    const color = quote?.coverColor || this.coverColor;
    const rows = [];
    const outdoor = OUTDOOR[quote?.installPlan];
    if (outdoor) {
      rows.push([
        '室外の配管カバー',
        `${outdoor}${color && quote.installPlan === 'cover' ? `（${color}）` : ''}`,
      ]);
    }
    // 室内は「カバーがある部屋」でだけ選ばれるので、選ばれていなければ行を出さない
    const indoor = INDOOR[quote?.interiorCoverPlan];
    if (indoor) rows.push(['室内の配管カバー', indoor]);
    /**
     * ★ 架台はここに出さない（日高さん指摘 2026-10-08・#1121）★
     *   以前は「室外機の架台：屋根置き（交換）」をこの行に足していたが、この行は
     *   カートと注文画面で**標準工事の行**のプロパティになる。架台交換は標準工事ではなく
     *   追加工事（「屋根置き（架台交換） ¥13,200」の行）なので、標準工事に含まれるように読める。
     *   架台は追加工事の費目名で、置き方と状態まで分かる（`confirmQuote.service.ts` の `placementLabel`）。
     */
    return rows;
  }

  /**
   * 選んだ配管カバーを1行で出す。
   *
   * ★ 金額の内訳には出ない ★
   * カバー費は追加工事ではなく**標準工事費（プラン価格）に含まれる**
   * （backend の composeBundlePlanPrice）。この行が無いと、画面は
   * 「追加工事はありません」なのに標準工事費だけ 5,500円 高い状態になり、
   * **なぜ高いのかを確かめる手段が無くなる**。
   */
  coverLineHtml(quote) {
    const rows = this.coverRows(quote);
    if (rows.length === 0) return '';
    return rows
      .map(
        ([label, value]) => `
      <div class="aicon-iq__coverPicked">
        <span class="aicon-iq__coverPickedLabel">${this.escape(label)}</span>
        <span class="aicon-iq__coverPickedValue">${this.escape(value)}</span>
      </div>
    `,
      )
      .join('');
  }

  renderQuote(quote) {
    // 確定した見積り。checkout() がカートの行を組むのに使う（2026-08-20）
    this.confirmedQuote = quote;
    const hasExtra = quote.additionalItems.length > 0;

    this.resultRoot.innerHTML = `
      ${
        hasExtra
          ? `<h3 class="aicon-iq__resultTitle">追加工事が${quote.additionalItems.length}点必要です</h3>
             <p class="aicon-iq__lead">
               写真から判断した内容と金額です。ここでご了承いただいた金額のまま設置します。当日に増えることはありません。
             </p>
             <ul class="aicon-iq__items">
               ${quote.additionalItems.map((item) => this.itemHtml(item)).join('')}
             </ul>`
          : `<div class="aicon-iq__okMark" aria-hidden="true">✓</div>
             <h3 class="aicon-iq__resultTitle is-center">追加工事はありません</h3>
             <p class="aicon-iq__lead is-center">
               標準工事の範囲で設置できます。表示中の総額のまま、現地での追加請求はありません。
             </p>`
      }

      ${this.coverLineHtml(quote)}

      <div class="aicon-iq__totalCard">
        <p class="aicon-iq__totalLabel">
          ${hasExtra ? '追加工事を含む総額（税込）' : '確定した総額（税込）'}
        </p>
        <p class="aicon-iq__totalValue">${this.yen(quote.total)}<span>円</span></p>
        <dl class="aicon-iq__breakdown">
          <div><dt>本体</dt><dd>${this.yen(quote.productPrice)}円</dd></div>
          <div><dt>標準工事費</dt><dd>${this.yen(quote.standardInstallFee)}円</dd></div>
          <div class="${hasExtra ? 'is-extra' : ''}">
            <dt>追加工事${hasExtra ? ` ${quote.additionalItems.length}点` : ''}</dt>
            <dd>${hasExtra ? '+' : ''}${this.yen(quote.additionalTotal)}円</dd>
          </div>
          ${
            quote.photoDiscount > 0
              ? `<div class="is-discount">
                   <dt>写真アップロード割引</dt>
                   <dd>−${this.yen(quote.photoDiscount)}円</dd>
                 </div>`
              : ''
          }
        </dl>
      </div>

      ${this.resultNoticeHtml()}

      ${
        /*
         * ★ 判定結果からカートへ進む唯一のボタン（日高さん指摘 2026-08-21 で復活）★
         *
         * checkout() は [data-iq-checkout] を探すが、**このボタンの HTML が
         * どこにも無かった**。「担当者に相談する」を撤去した際に、正規の CTA も
         * 一緒に消えていた（checkout() 本体とリスナーの配線だけが残っていた）。
         * 結果、追加工事を確定したのに次へ進めず、判定結果が行き止まりになっていた。
         *
         * ★ contactStaff() ではなく checkout() を呼ぶ ★
         * contactStaff() は本体＋標準工事の2行しか入れないため、確定した
         * 追加工事・写真アップロード割引が落ちる（例: 表示 173,800円 →
         * カート 148,400円。差額 25,400円は請求できない）。
         * checkout() は buildCartItems() が追加工事も割引も組むので金額が一致する。
         */
        '<button type="button" class="aicon-iq__cta" data-iq-checkout>この内容でカートに入れる ›</button>'
      }

      ${
        // ★ ここに「担当者に相談する」は置かない（日高さん判断 2026-08-20）★
        //   以前は contactStaff() を呼んでいたが、相談ではなく**カートに入れて /cart へ移動**
        //   するだけだった。しかもカートは本体＋標準工事の2行しか持てないため、
        //   確定したはずの 追加工事・写真アップロード割引・銀行振込割引が全部落ちる。
        //   さらに `_担当者確認: 追加工事の要否を確認中` が付き、AI が要否を確定した直後の
        //   注文なのに担当者へ「確認中」と伝わっていた。
        //
        //   金額が確定している画面から出せる2つ目の行き先は「購入しないもの」しかない。
        //   相談の導線は全ページのチャット（Shopify Inbox）とフッターの電話・メールが持つ。
        ''
      }
    `;

    this.resultRoot
      .querySelector('[data-iq-checkout]')
      ?.addEventListener('click', () => this.checkout());
  }

  /**
   * 判定結果の注意書き（2026-09-19）。
   *
   * ★ ここは写真診断を通した方しか通らない ★
   *   `renderQuote()` を呼ぶ `enterResult()` は `analysis.status === 'analyzed'`
   *   でなければ `renderInconclusive()` へ抜ける。∴ 出し分けの条件は要らない。
   *   購入ボックスの「工事条件（2階→1階）」の申告だけで進んだ方はこの画面に来ない。
   *
   * ★ 文言は data-* から取る ★
   *   同じ内容がカート（sections/aicon-cart-breakdown.liquid）にもある。
   *   JS に焼き込むと、片方が設定・片方がコードになり同期できなくなる。
   *   設定が空なら何も出さない（空の枠を残さない）。
   *
   * ★ CTA の直前に置く ★
   *   総額とエリアを読んだあと、カートへ進む判断をする直前に目に入る位置。
   *   総額カードより上に置くと、金額より先に但し書きを読ませることになる。
   */
  resultNoticeHtml() {
    const lead = (this.dataset.resultNotice || '').trim();
    const refund = (this.dataset.resultNoticeRefund || '').trim();
    const tel = (this.dataset.resultNoticeTel || '').trim();
    const concealed = (this.dataset.resultNoticeConcealed || '').trim();
    if (!lead && !refund && !tel && !concealed) return '';
    return `
      <div class="aicon-iq__resultNotice">
        ${lead ? `<p class="aicon-iq__resultNoticeLead">${this.escape(lead)}</p>` : ''}
        ${concealed ? this.resultNoticeConcealedHtml(concealed, tel) : this.resultNoticeTelHtml(tel)}
        ${refund ? `<p class="aicon-iq__resultNoticeRefund">${this.escape(refund)}</p>` : ''}
      </div>
    `;
  }

  /**
   * 隠蔽配管についての注意書きと、メールの導線（日高さん指示 2026-09-22）。
   *
   * ★ なぜ要るか ★
   *   隠蔽配管は**工事はできるが、写真だけでは内容と費用が判定できない**。
   *   技術スタッフが現地を見ないと分からない領域で、いまの画像診断の範囲外。
   *   ここで触れずに総額だけ出すと、写真診断を通った方が
   *   「この金額で隠蔽配管もやってもらえる」と読むことになる。
   *   FAQ には既に「隠蔽配管工事などは担当者が現地で確認のうえお見積もりします」と
   *   書いてあるが（templates/product.json）、**そこから連絡する導線が無かった**。
   *   在庫確認の電話導線（2026-09-14）と同じ形で、押せる面として置く。
   *
   * ★ 写真診断を通した方にだけ出す（日高さん指示 2026-09-23）★
   *   商品ページの独立セクション（aicon-concealed-note・全員に表示）は撤去し、
   *   この判定結果の画面だけに戻した。出し分けは resultNoticeHtml() と同じく
   *   「ここへ来るのは analyzed の方だけ」で担保している。
   *
   * ★ 電話とメールは案内文の下にまとめて置く ★
   *   文が「お電話またはメールでご相談ください」で終わるので、その直後に両方を並べる。
   *   電話は上のリード文（画像に不安がある場合はお電話で）とも共用する1つだけで、
   *   2つ目は出さない。メールは営業時間外の方の連絡手段（受付は平日 9:00〜17:00）。
   *
   * ★ 文が空ならメールごと出さない ★
   *   「お問い合わせください」と書いていないのにメールのボタンだけ残ると、
   *   何のための宛先か分からない。
   */
  resultNoticeConcealedHtml(concealed, tel) {
    if (!concealed) return '';
    const email = (this.dataset.resultNoticeEmail || '').trim();
    const label = (this.dataset.resultNoticeEmailLabel || 'メールで問い合わせる').trim();
    return `
      <div class="aicon-iq__resultNoticeConcealed">
        <p class="aicon-iq__resultNoticeConcealedText">${this.escape(concealed)}</p>
        <div class="aicon-iq__resultNoticeContacts">
        ${this.resultNoticeTelHtml(tel)}
        ${
          email
            ? `<a class="aicon-iq__resultNoticeMail" href="mailto:${this.escape(email)}">
                 <svg class="aicon-iq__resultNoticeMailIcon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
                   <path fill="currentColor" d="M4 5h16c.6 0 1 .4 1 1v12c0 .6-.4 1-1 1H4c-.6 0-1-.4-1-1V6c0-.6.4-1 1-1Zm8 7.2L5.4 7h13.2L12 12.2ZM5 9.1V17h14V9.1l-6.4 5.1c-.4.3-.8.3-1.2 0L5 9.1Z"/>
                 </svg>
                 <span class="aicon-iq__resultNoticeMailText">
                   <span class="aicon-iq__resultNoticeMailLabel">${this.escape(label)}</span>
                   <span class="aicon-iq__resultNoticeMailAddress">${this.escape(email)}</span>
                 </span>
               </a>`
            : ''
        }
        </div>
      </div>
    `;
  }

  /**
   * 注意書きの中の電話の導線。
   *
   * ★ 発信は数字だけにする ★
   *   ハイフン入りの tel: は一部の端末で発信できない
   *   （sections/aicon-company.liquid / aicon-footer.liquid と同じ整形）。
   *   表示は設定に入れたまま（053-533-9207）を出す。
   *
   * ★ 落とす文字を列挙しない（2026-09-19 レビュー）★
   *   Liquid 側は `remove: '-' | remove: ' '` しかできず、こちらだけ全角ハイフンや
   *   ダッシュ類を足すと **同じ番号なのに画面ごとに href が変わる**（二経路 drift）。
   *   数字以外を落とす1行にすれば、両者が同じ結果になる条件は
   *   「設定値が数字とハイフンだけ」に収まり、それは check-notice-tel.mjs が CI で担保する。
   *
   * ★ 番号が空なら丸ごと出さない ★
   *   「お電話にて」と書いてあるのに押せない行が残るより、出さないほうがよい。
   */
  resultNoticeTelHtml(tel) {
    if (!tel) return '';
    const href = tel.replace(/\D/g, '');
    const label = (this.dataset.resultNoticeTelLabel || 'お電話で相談する').trim();
    const hours = (this.dataset.resultNoticeTelHours || '').trim();
    return `
      <a class="aicon-iq__resultNoticeTel" href="tel:${this.escape(href)}">
        <svg class="aicon-iq__resultNoticeTelIcon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
          <path fill="currentColor" d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.2.4 2.4.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.2 1l-2.3 2.2Z"/>
        </svg>
        <span class="aicon-iq__resultNoticeTelBody">
          <span class="aicon-iq__resultNoticeTelText">
            <span class="aicon-iq__resultNoticeTelLabel">${this.escape(label)}</span>
            <span class="aicon-iq__resultNoticeTelNumber">${this.escape(tel)}</span>
          </span>
          ${hours ? `<span class="aicon-iq__resultNoticeTelHours">${this.escape(hours)}</span>` : ''}
        </span>
      </a>
    `;
  }

  itemHtml(item) {
    return `
      <li class="aicon-iq__item">
        <div class="aicon-iq__itemMain">
          <p class="aicon-iq__itemLabel">${this.escape(item.label)}</p>
          <p class="aicon-iq__itemPrice">+${this.yen(item.amount)}円</p>
        </div>
        ${item.note ? `<p class="aicon-iq__itemNote">${this.escape(item.note)}</p>` : ''}
      </li>
    `;
  }

  /**
   * 判定できなかったとき（Figma 画面08）。
   *
   * ★ 行き止まりにしない ★
   * 「判断できませんでした」で終わらせず、このあとの流れを示して担当者確認へ繋ぐ（REQ-102）。
   */
  renderInconclusive() {
    const reason = this.analysis?.reason;
    const canRetake = this.photos.size > 0 && this.canUsePhotos();

    this.resultRoot.innerHTML = `
      <h3 class="aicon-iq__resultTitle">写真だけでは判断できませんでした</h3>
      <p class="aicon-iq__lead">
        ${reason ? `${this.escape(reason)}。` : ''}担当者が状況を確認します。追加費用が出る場合は、工事の着手前に必ずご説明し、ご了承いただいてから進めます。
      </p>
      <ol class="aicon-iq__flow">
        <li><span>1</span>ご注文後1営業日以内に、担当者からお電話またはメールでご連絡します</li>
        <li><span>2</span>設置場所の状況をうかがい、追加工事の要否と金額を確定します</li>
        <li><span>3</span>ご了承いただいたうえで工事日を決めます。金額にご納得いかない場合はキャンセルできます</li>
      </ol>
      <button type="button" class="aicon-iq__cta" data-iq-contact>この内容でカートに入れる ›</button>
      ${
        // ★ 撮り直せる人にだけ出す（2026-08-20）★
        //   新規取り付けは写真判定そのものが対象外（canUsePhotos）で、
        //   1枚も出していない場合は「撮り直す」対象が存在しない。
        //   どちらでも出していたため「新規設置なのに撮り直しを促される」状態だった。
        canRetake
          ? '<button type="button" class="aicon-iq__ghost" data-iq-back-photos>写真を撮り直す</button>'
          : ''
      }
    `;

    this.resultRoot
      .querySelector('[data-iq-contact]')
      ?.addEventListener('click', (event) => this.contactStaff(event.currentTarget));
    this.resultRoot
      .querySelector('[data-iq-back-photos]')
      ?.addEventListener('click', () => this.goTo(2));
  }

  /** 数値を桁区切りにする。**丸めない**（サーバーの値をそのまま出す） */
  yen(value) {
    return Number(value ?? 0).toLocaleString('ja-JP');
  }

  // ------------------------------------------------------------------
  // STEP 4 相当: 決済ページへ / 担当者へ
  // ------------------------------------------------------------------

  /**
   * Draft Order を作って決済ページへ送る。
   *
   * ★ Figma の「カートに入れる」ではない ★
   * 追加工事は Shopify のバリアントに存在しないため custom line item として
   * Draft Order に積む。カート経由では表現できないので、決済ページへ直接遷移する。
   *
   * ★ 二重押下しても2件作られない ★
   * backend が `Quote.shopifyDraftOrderId` を冪等キーにしている。こちらでもボタンを
   * 無効化して、無用なリクエストを出さない。
   */
  /**
   * 確定した見積りから、カートに入れる行を組む（2026-08-20）。
   *
   * ★ 見せた金額と請求額を必ず一致させる ★
   * 工事の行はバリアント価格で課金されるので、`standardInstallFee - photoDiscount` と
   * 一致することを確かめてから使う。食い違ったまま入れると、確定画面で見せた総額と
   * 請求額がずれる。ずれたら**入れずに例外**にする（安い側にも高い側にも倒さない）。
   *
   * ★ 追加工事は「単価110円 × 数量」★
   * 価格マスタの全費目が110の倍数なので誤差なく表せる。割り切れない額が来たら
   * 価格マスタが想定外の値に変わったということなので、丸めずに例外にする。
   *
   * @returns /cart/add.js の items
   * @throws 金額が一致しない・必要なバリアントが無いとき
   */
  /**
   * 本体と工事（追加工事・延長保証）を1組として結ぶ ID を作る。
   *
   * ★ 1回のカート投入 ＝ 1組 ★
   *   assets/aicon-cart-pair.js が、片方が減らされたときに**同じ組の中から**
   *   相手を減らす。組が無いと、機種の違う2台をカートに入れたときに
   *   別の機種の本体・工事を取り違えて消す（同ファイルの groupBySet 参照）。
   *
   * ★ 同じ機種を2回入れると2行に分かれる ★
   *   Shopify はバリアントと properties が完全一致したときだけ行をまとめる。
   *   組ID が違えば別行になる。1台ごとに工事が紐づくので、これが正しい。
   *
   * @returns {string} 8文字程度の ID
   */
  newAiconSetId() {
    try {
      const uuid = window.crypto?.randomUUID?.();
      if (uuid) return uuid.slice(0, 8);
    } catch (_e) {
      // crypto を触れない環境。下の組み立てに落ちる
    }
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  }

  /**
   * 工事の行に残す「機種の発売年」。記録が無い（古い見積）ときは何も残さない。
   * 年が読めないときは「未確認」とし、スタッフが型番から確かめる。
   */
  releaseYearNote(quote) {
    const check = quote?.releaseYear;
    if (!check) return '';
    const year = parseInt(check.releaseYear || '', 10);
    if (check.known === true && Number.isFinite(year)) return `${year}年`;
    return '未確認（12年の判定が未実施・スタッフ確認）';
  }

  buildCartItems() {
    const q = this.confirmedQuote;
    if (!q) {
      // ★ 行き止まりにしない ★
      //   同じ画面で押し直しても解けないので、下の catch がセッションを捨てて
      //   エリア判定からやり直せるよう nextAction を付ける。
      const err = new Error('お手続きの情報が見つかりませんでした。最初からやり直してください。');
      err.nextAction = 'reopen_modal';
      throw err;
    }

    const isNew = this.currentInstallType() === 'new';
    const bodyId = (document.querySelector('[data-aicon-methods] input:checked')?.dataset.bodyId || '').trim();
    const installId = ((isNew ? this.dataset.installNewPhotoId : this.dataset.installReplacePhotoId) || '').trim();
    const installPrice = Number(isNew ? this.dataset.installNewPhotoPrice : this.dataset.installReplacePhotoPrice);

    if (!bodyId || !installId || !Number.isFinite(installPrice)) {
      throw new Error('お手続きを進められませんでした。お手数ですが、商品ページからお試しください。');
    }

    // Shopify の price は最小通貨単位（円 × 100）で入る
    const yen = (minor) => Math.round(minor / 100);
    const discount = Number(q.photoDiscount) || 0;
    const expectedInstall = (Number(q.standardInstallFee) || 0) - discount;
    if (yen(installPrice) !== expectedInstall) {
      // 工事プランが standard 以外（外化粧カバー等）だとここに来る。黙って課金しない
      throw new Error('金額の確認が取れませんでした。お手数ですが、お問い合わせからご連絡ください。');
    }

    // ★ この投入ぶんを1組として結ぶ（2026-08-25・複数台）★
    //   本体・工事・追加工事・保証の4行すべてに同じ組ID を載せる。
    //   assets/aicon-cart-pair.js がこの ID で相方を引く。
    const setId = this.newAiconSetId();

    // ★ 選んだ配管カバーを工事の行に載せる（2026-10-05）★
    //   再利用か新品かは見積（AiCON の DB）にしか無く、Shopify の注文画面では
    //   お店の人が確かめられなかった。新品・再利用の費目（confirmQuote の
    //   exteriorCoverFeeOf）は Cart Transform で「追加工事 一式」にまとめられて
    //   一目では読めず、テープ巻きと室内カバーなしは金額の行そのものが無い。
    //   キーを `_` で始めないのは、注文画面とカートの両方に出すため
    //   （お客様にとっても自分が選んだ仕上げの控えになる）。
    const installProperties = { _aicon_set: setId };
    for (const [label, value] of this.coverRows(q)) installProperties[label] = value;

    // ★ 機種の発売年をお店に残す（#1117 / 2026-10-08）★
    //   配管カバーの再利用不可・架台の交換必須は「発売から12年」で決まる。発売年が
    //   取れなかった注文は、この判定が働いていない（金額は従来どおりで確定している）。
    //   注文画面で見分けられるよう工事の行に残す。`_` 始まりなのでカートには出ない
    //   （cart-products.liquid が `_` 始まりを伏せる）。判定そのものは backend が持つ。
    const releaseYearNote = this.releaseYearNote(q);
    if (releaseYearNote) installProperties._機種の発売年 = releaseYearNote;

    const items = [
      { id: Number(bodyId), quantity: 1, properties: { _aicon_set: setId } },
      { id: Number(installId), quantity: 1, properties: installProperties },
    ];

    // 追加工事。費目ごとに properties を付けると別行になり、カートに費目名が出る
    const additionalId = (this.dataset.additionalId || '').trim();
    const unit = Number(this.dataset.additionalUnit);
    for (const item of q.additionalItems || []) {
      const amount = Number(item.amount) || 0;
      if (amount <= 0) continue;
      if (!additionalId || !Number.isFinite(unit) || unit <= 0) {
        throw new Error('追加工事をお申し込みできませんでした。お手数ですが、お問い合わせからご連絡ください。');
      }
      const unitYen = yen(unit);
      if (amount % unitYen !== 0) {
        // 価格マスタが 110 の倍数でない値に変えられた。丸めると請求額が変わる
        throw new Error('金額の確認が取れませんでした。お手数ですが、お問い合わせからご連絡ください。');
      }
      items.push({
        id: Number(additionalId),
        quantity: amount / unitYen,
        properties: { 費目: String(item.label || '追加工事'), _aicon_set: setId },
      });
    }

    // 延長保証（任意）。購入ボックスで選ばれていれば一緒に入れる
    const warrantyId = (document.querySelector('[data-aicon-warranty-select]')?.value || '').trim();
    if (warrantyId) items.push({ id: Number(warrantyId), quantity: 1, properties: { _aicon_set: setId } });

    return items;
  }

  /**
   * カートから AiCON の行（本体・標準工事・追加工事・延長保証）を消す。
   *
   * ★ 消してから入れる ★
   * cart/add.js は追加なので、診断のやり直しや「先に本体だけ入れた」状態のまま
   * 足すと同じものが2行になり、数量2で請求される。
   *
   * 掃除に失敗しても購入は止めない。重複より「買えない」ほうが損失が大きく、
   * 重複はカート画面でお客様が気づいて直せる（aicon-return.js と同じ判断）。
   */
  async clearAiconLines() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const managed = ['aicon-standard-install', 'aicon-additional-work', 'aicon-extended-warranty'];
    try {
      const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const modelHandle = this.dataset.productHandle || '';
      for (const line of cart.items || []) {
        // 本体は「いま見ている機種」だけを消す。別の機種を検討中のお客様の行は触らない
        const isManaged = managed.includes(line.handle) || (modelHandle !== '' && line.handle === modelHandle);
        if (!isManaged) continue;
        await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        });
      }
    } catch (_e) {
      // best-effort。ここで例外を投げると購入まで巻き添えで止まる
    }
  }

  async checkout() {
    const button = this.resultRoot.querySelector('[data-iq-checkout]');
    if (!button || button.disabled) return;

    this.holdButton(button, '購入手続きを準備しています…');
    this.clearError();

    try {
      // ★ カートへ入れる（2026-08-20）★
      //   従来は Draft Order の請求リンクへ飛ばしていたが、
      //   (1) write_draft_orders が prod 未付与で動かない
      //   (2) 内訳をカートで見せたい（日高さん指摘）
      //   の2点から、通常のカート → Shopify チェックアウトに切り替えた。
      //   金額は「単価110円 × 数量」で追加工事も正確に表せる（additional-work-product.ts）。
      const items = this.buildCartItems();
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      // ★ 同じ商品の既存行を先に消す ★
      //   cart/add.js は「追加」なので、診断をやり直した／先に本体だけ入れていた
      //   お客様のカートに同じ行が積み増される。数量2で決済されると1台しか要らないのに
      //   2台分を請求することになる（assets/aicon-return.js が同じ理由で消している）。
      await this.clearAiconLines();

      // ★ 属性はカート投入より先に書く ★
      //   後にすると、投入直後に決済へ進んだ場合に紐付けが漏れる。
      //   Draft Order をやめたことで note に見積IDを載せる経路が無くなったため、
      //   ここで cart attributes に入れないと注文 webhook が見積を回収できない
      //   （backend/src/api/webhooks/shopify.ts が quoteId / sessionId を見ている）。
      //   属性名は order-webhook.service.ts の ATTR_QUOTE_ID / ATTR_SESSION_ID と一致必須。
      const attrRes = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: {
            // 値が無いときも空文字で上書きする。渡したキーだけが更新される（マージ）ため、
            // 条件付きにすると前回の診断のIDが残って無関係な見積と紐付く
            __aicon_quote_id: String(this.confirmedQuote?.quoteId || ''),
            __aicon_session_id: String(this.session?.quoteId || ''),
          },
        }),
      });
      // 紐付けの無い注文が立つと、どの診断の注文か後から辿れなくなる。ここで落ちたら進めない
      if (!attrRes.ok) throw new Error(`cart/update ${attrRes.status}`);

      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error(`cart/add ${res.status}`);

      // 見積の diagnosisQuoteId とは別に、注文にも残す（写真なしの経路と同じ形で辿れるように）
      await this.tagCartWithDiagnosis();

      // 内訳を確認してから決済へ進んでいただく。カートに内訳セクションがある
      window.location.href = `${root}cart`;
      return;
    } catch (error) {
      this.releaseButton(button);

      // ★ 行き止まりにしない ★
      //   同じ文言を出してボタンを押し直させても、原因が解消しないものがある。
      //   Draft Order をやめてカート投入に変えたため、ここへ届くのは buildCartItems と
      //   cart/* の失敗だけになった（proxy の nextAction は来ない）。
      //   復帰できるケースには buildCartItems 側で nextAction を付けている。
      if (error.nextAction === 'reopen_modal') {
        // 見積が見つからない。セッションを捨ててエリア判定からやり直してもらう
        this.forgetSession();
        this.showError('お手続きの情報が見つかりませんでした。最初からやり直してください。');
        this.goTo(1);
        return;
      }
      this.showError(error.message || 'ただいま購入手続きへ進めません。時間をおいてお試しください。');
    }
  }

  /**
   * 担当者の確認へ回す。
   *
   * 追加工事の確定を待たずに本体を購入してもらい、工事の詳細は担当者が後から詰める。
   * 「本体のみ」ではなく**工事つき本体＋標準工事**を選ぶ（工事は行う。金額が未確定なだけ）。
   *
   * ★ 本体と工事は別商品の2行で入れる（2026-08-19 / D3）★
   * 以前は「標準工事セット」バリアント1行で済んでいたが、工事費を機種から
   * 切り離したため、本体（工事つき）＋ 標準工事 の2行を入れないと
   * **工事費が丸ごと請求漏れになる**。
   * 工事バリアントは取替え／新規で別（差 = 取り外し・回収 5,500円）。
   */
  async contactStaff(trigger) {
    // ★ カートへ入れる前に延長保証を一度おたずねする（日高さん指示 2026-08-21）★
    //   購入ボックスのオプション欄から移してきた訴求。ここを通るのは
    //   「写真なしで進む」「このままカートに入れる」の2つで、どちらも
    //   カートに入る直前なので、出す位置はここ1箇所で足りる。
    //   ボトムシートで答えが決まったら、同じ関数を decided=true で呼び直す。
    if (this.openWarrantySheet(() => this.contactStaff(trigger), trigger)) return;

    // 通信の往復があるので、押しっぱなしで2回カートに入らないようにする
    if (trigger) {
      if (trigger.disabled) return;
      this.holdButton(trigger);
    }

    const bodyId = (this.dataset.setVariantId || '').trim();
    const isNew = this.currentInstallType() === 'new';
    const installId = (
      (isNew ? this.dataset.installNewId : this.dataset.installReplaceId) || ''
    ).trim();

    // 本体か工事のどちらかが解決できない場合は、片側だけ入れて金額を誤らせない。
    if (!bodyId || !installId) {
      this.showError('お手数ですが、お問い合わせからご連絡ください。');
      this.releaseButton(trigger);
      return;
    }

    // ★ 延長保証を落とさない（2026-08-21 レビューで発見）★
    // 購入ボックスの submit リスナーは本体・工事・保証の3行を入れていた。
    // その横取りを1箇所に寄せた結果、この出口（写真なしで進む／このままカートに入れる）
    // が唯一のカート投入経路になる場面が生まれた。ここが保証を入れていなかったため、
    // 「工事つき＋延長保証」を選んだお客様の保証が黙って消える状態だった。
    // 写真判定を通る経路（buildItems）は元から保証を入れている。
    const warrantyId = (document.querySelector('[data-aicon-warranty-select]')?.value || '').trim();

    // ★ アンダースコア始まりのキーはカート・注文画面に出ない（Shopify の仕様）★
    //   「担当者確認: 追加工事の要否を確認中」がカートの行に出ていて、
    //   お客様には意味が分からない内部連絡になっていた（日高さん指摘 2026-08-20）。
    //   キーは残す（管理画面の注文明細では読めるため、担当者は取りこぼさない）。
    // ★ 組ID（2026-08-25・複数台）★
    //   この経路は clearAiconLines を呼ばないため、機種を変えて2回通ると
    //   2台ぶんがカートに残る。組ID が無いと、片方を消したときに
    //   別の機種の本体・工事が消える（assets/aicon-cart-pair.js の groupBySet 参照）。
    const setId = this.newAiconSetId();
    const properties = { _担当者確認: '追加工事の要否を確認中', _aicon_set: setId };

    /*
     * ★ 2階→1階の追加工事を落とさない（2026-08-31）★
     * ここは写真判定を通さない出口で、**2階→1階を選んだ人の主な経路**になる。
     * 入れ忘れると、画面で「+13,090円」と見せた総額より安い額で決済され、
     * 差額は現地での追加請求になる（今回の対応で無くしたかった状態そのもの）。
     * 延長保証で同じ抜けを踏んでいる（すぐ上の ★ 参照）。
     */
    const presetItems = this.downflowItems(setId);
    if (this.downflowSelected() && presetItems.length === 0) {
      // 黙って標準工事の額で売らない
      this.showError('工事条件をお申し込みできませんでした。お手数ですが、お問い合わせからご連絡ください。');
      this.releaseButton(trigger);
      return;
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            { id: Number(bodyId), quantity: 1, properties },
            { id: Number(installId), quantity: 1, properties },
          ]
            .concat(presetItems)
            .concat(warrantyId ? [{ id: Number(warrantyId), quantity: 1, properties: { _aicon_set: setId } }] : []),
        }),
      });
      if (!res.ok) throw new Error(`cart/add ${res.status}`);
    } catch (_e) {
      this.showError('カートに入れられませんでした。お手数ですが、もう一度お試しください。');
      this.releaseButton(trigger);
      return;
    }

    // 写真見積を通らないので見積に残せない。注文に残すためカートに載せる（#1170）
    await this.tagCartWithDiagnosis();

    // 移動先も routes.root 基準にする（同じファイルの他の箇所と揃える・PR-Agent /improve）
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    window.location.href = `${root}cart`;
  }

  // ------------------------------------------------------------------
  // エラー表示
  // ------------------------------------------------------------------

  showError(message) {
    if (!this.errorBox) return;
    this.errorBox.textContent = message;
    this.errorBox.hidden = false;
  }

  clearError() {
    if (!this.errorBox) return;
    this.errorBox.textContent = '';
    this.errorBox.hidden = true;
  }
}

if (!customElements.get('aicon-install-quote')) {
  customElements.define('aicon-install-quote', AiconInstallQuote);
}
